<template>
  <div class="setup-page">
    <div class="setup-header">
      <button
        v-for="step in steps"
        :key="step.id"
        class="step"
        :class="{ active: activeStep === step.id }"
        @click="goToStep(step.id)"
      >
        {{ step.label }}
      </button>
    </div>

    <div class="setup-fixed-actions">
      <button
        v-if="step1Generated"
        type="button"
        class="secondary-btn slim-btn"
        @click="openScriptPreviewModal"
      >
        预览剧本
      </button>
      <button
        v-if="step1Generated && activeStep === 1"
        type="button"
        class="secondary-btn slim-btn"
        :disabled="step1Regenerating || step1Generating"
        @click="handleStep1RegenerateReset"
      >
        {{ step1Regenerating ? "清空中…" : "重新生成剧本" }}
      </button>
      <template v-if="activeStep === 1">
        <button
          v-if="!step1Locked && !step1Generated"
          type="button"
          class="main-btn slim-btn"
          :disabled="step1GenerateDisabled"
          @click="handleStep1Generate"
        >
          {{
            step1Generating
              ? "生成中…"
              : `开始生成（${step1PointsLabel}）`
          }}
        </button>
        <span v-if="!step1Locked && !step1Generated && step1Generating" class="step1-processing-tip">
          处理中（可离开页面，刷新后会自动继续监听）
        </span>
        <button
          v-if="step1Generated"
          type="button"
          class="main-btn slim-btn"
          @click="goToStep(2)"
        >
          下一步
        </button>
      </template>
      <template v-else>
        <button v-if="activeStep > 1" type="button" class="secondary-btn slim-btn" @click="goToStep(activeStep - 1)">
          上一步
        </button>
        <button
          v-if="activeStep < 3"
          type="button"
          class="main-btn slim-btn"
          :disabled="activeStep === 2 && !step2Ready"
          @click="goToStep(activeStep + 1)"
        >
          下一步
        </button>
        <!-- “进入分镜编辑”按钮移除：每集卡片的“编辑”会进入对应编辑页 -->
      </template>
    </div>

    <div v-if="activeStep === 1" class="setup-body step-fill">
      <aside class="setup-sidebar">
        <div class="setup-block">
          <div class="setup-label">项目名称</div>
          <input
            v-model.trim="project.title"
            class="setup-title-input"
            maxlength="50"
            placeholder="请输入项目名称"
            @blur="persistProjectTitle"
          />
        </div>
        <div class="setup-block">
          <div class="setup-label">画幅</div>
          <div class="setup-select-grid setup-ratio-grid">
            <button
              v-for="item in ratioOptions"
              :key="item.value"
              type="button"
              class="setup-select-card"
              :class="{ active: project.ratio === item.value }"
              @click="setProjectRatio(item.value)"
            >
              {{ item.label }}
            </button>
          </div>
        </div>
        <div class="setup-block">
          <div class="setup-label">类型</div>
          <div class="setup-select-grid">
            <button
              v-for="item in typeOptions"
              :key="item.value"
              type="button"
              class="setup-select-card setup-type-card"
              :class="{ active: project.type === item.value }"
              @click="setProjectType(item.value)"
            >
              <strong>{{ item.label }}</strong>
              <span>{{ item.desc }}</span>
            </button>
          </div>
        </div>
        <div class="setup-block">
			<!-- 视频生成语言 -->
          <div class="setup-label">视频语言</div>
          <div class="setup-select-grid setup-ratio-grid">
            <button
              v-for="item in videoLangOptions"
              :key="item.value"
              type="button"
              class="setup-select-card"
              :class="{ active: projectVideoLang === item.value }"
              @click="setProjectVideoLang(item.value)"
            >
              {{ item.label }}
            </button>
          </div>
          <!-- <div class="setup-hint">用于步骤一与步骤四视频模型提示词（如 Grok 偏英文、Seedance 偏中文时可显式约束）。</div> -->
        </div>
        <div class="setup-block">
          <div class="setup-label">画面风格</div>
          <div class="style-grid">
            <div
              v-for="item in styleOptions"
              :key="item.id"
              class="style-card"
              :class="{ active: item.id === activeStyle }"
              @click="setActiveStyle(item.id)"
            >
              <img
                v-if="String(item.cover || '').trim()"
                class="style-card-cover"
                :src="item.cover"
                :alt="item.name"
                @error="item.cover = ''"
              />
              <div v-else class="style-card-cover placeholder-block"></div>
              <span>{{ item.name }}</span>
            </div>
          </div>
        </div>
      </aside>

      <section class="setup-main">
        <h2>剧本</h2>
        <p class="hint">上传您的剧本或小说，开始创作</p>
        <div class="script-type-row">
          <button
            v-for="type in scriptTypes"
            :key="type.value"
            type="button"
            class="script-type"
            :class="{ active: activeScriptType === type.value, disabled: scriptTypeLocked }"
            @click="onScriptTypeClick(type.value)"
          >
            {{ type.label }}
          </button>
        </div>
        <p
          v-if="!step1Locked && !step1Generated && activeScriptType === 'ai_generate_script_agent'"
          class="hint"
        >
          AI生成通常需 1～3 分钟，请耐心等待。
        </p>
        <textarea
          v-model.trim="scriptContent"
          class="script-editor"
          :class="{ disabled: step1Locked }"
          :placeholder="scriptPlaceholder"
          :readonly="step1Locked"
        />
        <div
          v-if="!step1Locked && !step1Generated && activeScriptType === 'upload_script'"
          class="script-char-count"
          :class="{ over: scriptContentOverLimit }"
        >
          {{ scriptContentCharCount }} / {{ step1ScriptMaxChars }} 字
          <span v-if="scriptContentOverLimit" class="script-char-hint">超出上限，请删减或分段创作后再提交</span>
        </div>
        <div v-if="!step1Locked" class="script-episode-block">
          <div class="setup-label">预设集数</div>
          <p class="hint episode-count-hint">
            与后台步骤一一致：智能集数约 {{ step1EpisodeSmartDefault }} 集；自定义上限 {{ step1EpisodeCustomMax }} 集（超出会自动收敛）。
          </p>
          <div class="episode-count-row">
            <div class="episode-radios">
              <label class="episode-radio-label">
                <input v-model="episodeCountMode" type="radio" value="smart" name="episodeCountMode" />
                <span>智能集数</span>
              </label>
              <div class="episode-custom-inline">
                <label class="episode-radio-label">
                  <input v-model="episodeCountMode" type="radio" value="custom" name="episodeCountMode" />
                  <span>自定义集数</span>
                </label>
                <div
                  v-show="episodeCountMode === 'custom'"
                  class="episode-count-input-wrap"
                >
                  <input
                    v-model.number="customEpisodeCount"
                    type="number"
                    min="1"
                    :max="step1EpisodeCustomMax"
                    class="episode-count-input"
                    @change="clampCustomEpisodeCount"
                    @blur="clampCustomEpisodeCount"
                  />
                  <span class="episode-count-suffix">集</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>

    <div v-else-if="activeStep === 2" class="role-layout step-fill">
      <aside class="role-list-panel">
        <div class="role-panel-header">
          <h3>{{ resourcePanelTitle }}</h3>
          <div class="role-header-actions">
            <button class="secondary-btn slim-btn" @click="openRoleModal()"  v-if="false">+ 添加角色</button>
            <button class="secondary-btn slim-btn" :disabled="step2ImageWorkLocked" @click="handleBatchGenerate">
              {{
                batchGenerating
                  ? "批量生成中…"
                  : `批量生成（${batchImagePointsSummary}）`
              }}
            </button>
          </div>
        </div>
        <div v-if="step2WatchList.length" class="step2-task-queue-hint">
          {{ step2QueueHintText }}
        </div>
        <div class="resource-subnav" role="tablist">
          <button
            type="button"
            class="resource-subnav-item"
            :class="{ active: resourceSubTab === 'role' }"
            role="tab"
            :aria-selected="resourceSubTab === 'role'"
            @click="setResourceSubTab('role')"
          >
            角色
          </button>
          <button
            type="button"
            class="resource-subnav-item"
            :class="{ active: resourceSubTab === 'scene' }"
            role="tab"
            :aria-selected="resourceSubTab === 'scene'"
            @click="setResourceSubTab('scene')"
          >
            场景
          </button>
          <button
            type="button"
            class="resource-subnav-item"
            :class="{ active: resourceSubTab === 'material' }"
            role="tab"
            :aria-selected="resourceSubTab === 'material'"
            @click="setResourceSubTab('material')"
          >
            素材
          </button>
        </div>
        <div v-if="!currentResourceList.length" class="empty-panel">暂无数据</div>
        <div v-else class="role-list">
          <div
            v-for="item in currentResourceList"
            :key="`${resourceSubTab}-${item.id}`"
            class="role-list-item"
            :class="{ active: isResourceItemActive(item) }"
            @click="selectResourceItem(item)"
          >
            <div v-if="isPlaceholderAsset(getResourceThumb(item))" class="role-list-thumb placeholder-block"></div>
            <img v-else class="role-list-thumb" :src="getResourceThumb(item)" :alt="item.name" @error="onImgError(item, 'avatar')" />
            <div class="role-list-content">
              <div class="role-list-top">
                <strong>{{ item.name }}</strong>
                <span class="role-status">{{ itemPreviewLabel(item) }}</span>
              </div>
              <p>{{ item.summary }}</p>
            </div>
            <button
              type="button"
              class="role-delete-icon"
              title="删除角色"
              @click.stop="confirmDeleteRole(item)"  v-if="false"
            >
              🗑
            </button>
          </div>
        </div>
      </aside>

      <section class="role-preview-panel">
        <div v-if="currentResourceEntity" class="role-preview-wrap">
          <img
            v-if="currentResourceEntity.preview && !isPlaceholderAsset(currentResourceEntity.preview)"
            class="role-preview-image"
            :src="currentResourceEntity.preview"
            :alt="currentResourceEntity.name"
            @click="openEntityPreview(currentResourceEntity)"
            @error="onImgError(currentResourceEntity, 'preview')"
          />
          <div v-else-if="currentResourceEntity.preview" class="role-preview-image placeholder-block"></div>
          <div v-else class="role-preview-placeholder">{{ previewPlaceholderText }}</div>
          <div class="role-preview-actions">
            <input
              ref="entityUploadInput"
              type="file"
              accept="image/*"
              style="display: none"
              @change="onEntityImageSelected"
            />
            <button class="secondary-btn slim-btn" @click="triggerEntityImageUpload">上传图片</button>
            <button class="secondary-btn slim-btn" v-if="false">对话改图</button>
            <button class="secondary-btn slim-btn" :disabled="!entityHasPreviewImage" @click="downloadCurrentEntityImage">
              下载图片
            </button>
            <button class="secondary-btn slim-btn" v-if="false">生成记录</button>
          </div>
        </div>
        <div v-else class="empty-panel role-empty">暂无数据</div>
      </section>

      <aside class="role-detail-panel">
        <h3>{{ resourceDetailTitle }}</h3>
        <div v-if="currentResourceEntity && resourceSubTab === 'role'" class="role-detail-form">
          <div class="detail-field">
            <label>角色名</label>
            <input v-model="currentResourceEntity.name" maxlength="20" />
          </div>
          <div class="detail-field">
            <label>年龄</label>
            <input v-model="currentResourceEntity.age" maxlength="20" />
          </div>
          <div class="detail-field">
            <label>性别</label>
            <input v-model="currentResourceEntity.gender" maxlength="20" />
          </div>
          <div class="detail-field">
            <label>描述</label>
            <textarea v-model="currentResourceEntity.description" rows="14" :placeholder="entityDescriptionPlaceholder" />
          </div>
          <div class="detail-actions">
            <button class="secondary-btn" @click="saveCurrentResource">保存角色信息</button>
            <button class="main-btn" :disabled="step2ImageWorkLocked" @click="regenerateCurrentResource">
              {{ step2CurrentEntityBusyLabel }}<span class="role-regenerate-points">（{{ step2SinglePointsLabel }}）</span>
            </button>
          </div>
        </div>
        <div v-else-if="currentResourceEntity && resourceSubTab === 'scene'" class="role-detail-form">
          <div class="detail-field">
            <label>场景名</label>
            <input v-model="currentResourceEntity.name" maxlength="20" />
          </div>
          <div class="detail-field">
            <label>摘要</label>
            <input v-model="currentResourceEntity.summary" maxlength="100" />
          </div>
          <div class="detail-field">
            <label>描述</label>
            <textarea v-model="currentResourceEntity.description" rows="14" :placeholder="entityDescriptionPlaceholder" />
          </div>
          <div class="detail-actions">
            <button class="secondary-btn" @click="saveCurrentResource">保存场景信息</button>
            <button class="main-btn" :disabled="step2ImageWorkLocked" @click="regenerateCurrentResource">
              {{ step2CurrentEntityBusyLabel }}<span class="role-regenerate-points">（{{ step2SinglePointsLabel }}）</span>
            </button>
          </div>
        </div>
        <div v-else-if="currentResourceEntity && resourceSubTab === 'material'" class="role-detail-form">
          <div class="detail-field">
            <label>素材名</label>
            <input v-model="currentResourceEntity.name" maxlength="20" />
          </div>
          <div class="detail-field">
            <label>摘要</label>
            <input v-model="currentResourceEntity.summary" maxlength="100" />
          </div>
          <div class="detail-field">
            <label>描述</label>
            <textarea v-model="currentResourceEntity.description" rows="14" :placeholder="entityDescriptionPlaceholder" />
          </div>
          <div class="detail-actions">
            <button class="secondary-btn" @click="saveCurrentResource">保存素材信息</button>
            <button class="main-btn" :disabled="step2ImageWorkLocked" @click="regenerateCurrentResource">
              {{ step2CurrentEntityBusyLabel }}<span class="role-regenerate-points">({{ step2SinglePointsLabel }})</span>
            </button>
          </div>
        </div>
        <div v-else class="empty-panel">暂无数据</div>
      </aside>
    </div>

    <div v-else class="storyboard-layout step-fill">
      <div class="storyboard-header">
        <div class="storyboard-header-left">
          <h2>共 {{ storyboards.length }} 集</h2>
          <p class="hint">分镜脚本生成后，可进行预览、编辑和导出。</p>
        </div>
        <div class="storyboard-header-actions">
          <button type="button" class="secondary-btn slim-btn" @click="openEpisodeModal">新增分集</button>
          <button type="button" class="secondary-btn slim-btn" @click="handleRegenerateStoryboardScript" v-if="false">
            {{ storyboards.length ? "重新生成分镜脚本" : "分镜生成" }}
          </button>
          <!-- “进入分镜编辑”按钮移除：每集卡片的“编辑”会进入对应编辑页 -->
        </div>
      </div>

      <div class="storyboard-content-scroll">
        <div v-if="!storyboards.length" class="empty-panel storyboard-empty">暂无数据</div>
        <div v-else class="storyboard-grid">
          <div v-for="(item, index) in storyboards" :key="item.id" class="storyboard-card">
            <div class="storyboard-index">{{ index + 1 }}</div>
            <div
              v-if="isPlaceholderAsset(item.cover)"
              class="storyboard-cover placeholder-block"
              @click="openStoryboardPreview(item)"
            ></div>
            <img
              v-else
              class="storyboard-cover"
              :src="item.cover"
              :alt="item.title"
              @click="openStoryboardPreview(item)"
              @error="onImgError(item, 'cover')"
            />
            <div class="storyboard-content">
              <h3 @click.stop="openStoryboardEdit(item)">{{ item.title }}</h3>
              <!-- 移除 roles/scenes/duration 展示 -->
              <div class="storyboard-actions">
                <button class="story-btn" @click.stop="openStoryboardEdit(item)">编辑</button>
                <button class="story-btn primary" @click.stop="openStoryboardExport(item)">导出</button>
                <button class="story-btn story-btn-danger" @click.stop="confirmDeleteStoryboard(item)">删除</button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <div v-if="roleModal.visible" class="mask" @click.self="closeRoleModal">
      <div class="modal modal-lg">
        <h3>{{ roleModal.mode === "create" ? "新增角色" : "编辑角色" }}</h3>
        <input v-model.trim="roleForm.name" maxlength="20" placeholder="角色名" />
        <input v-model.trim="roleForm.age" maxlength="20" placeholder="年龄" />
        <input v-model.trim="roleForm.gender" maxlength="20" placeholder="性别" />
        <input v-model.trim="roleForm.summary" maxlength="60" placeholder="角色摘要" />
        <textarea v-model.trim="roleForm.description" class="modal-textarea" rows="8" placeholder="请输入角色描述" />
        <div class="modal-actions">
          <button class="secondary-btn" @click="closeRoleModal">取消</button>
          <button class="main-btn modal-main-btn" @click="submitRoleModal">
            {{ roleModal.mode === "create" ? "确认新增" : "保存角色" }}
          </button>
        </div>
      </div>
    </div>

    <div v-if="episodeModal.visible" class="mask" @click.self="closeEpisodeModal">
      <div class="modal modal-lg episode-modal">
        <h3>新增分集</h3>
        <p class="hint">每次仅新增 1 集，在列表末尾追加。标题可填；分集剧本/脚本正文不超过 1 万字。</p>
        <div class="modal-field-block">
          <label>分集标题</label>
          <input v-model.trim="episodeModal.title" maxlength="80" placeholder="可选，留空则自动为「第 N 集：新分集」" />
        </div>
        <div class="modal-field-block">
          <label>本分集内容</label>
          <textarea
            v-model.trim="episodeModal.content"
            class="script-editor episode-modal-script"
            maxlength="10000"
            placeholder="请输入本分集剧本或分镜脚本内容，不超过1万字"
            rows="10"
          ></textarea>
          <div class="episode-content-count">{{ (episodeModal.content || "").length }}/10000</div>
        </div>
        <div class="modal-actions">
          <button type="button" class="secondary-btn" @click="closeEpisodeModal">取消</button>
          <button type="button" class="main-btn modal-main-btn" @click="submitEpisodeModal">确定</button>
        </div>
      </div>
    </div>

    <ImagePreviewLayer
      :visible="storyboardCoverPreview.visible"
      :src="storyboardCoverPreview.src"
      :alt="storyboardCoverPreview.alt"
      @close="closeStoryboardCoverPreview"
      @img-error="onStoryboardCoverPreviewImgError"
    />

    <ImagePreviewLayer
      :visible="entityPreviewDialog.visible"
      :src="entityPreviewDialog.src"
      :alt="entityPreviewDialog.alt"
      @close="closeEntityPreviewDialog"
      @img-error="onEntityPreviewImgError"
    />

    <ScriptPreviewModal
      :visible="scriptPreviewModalVisible"
      :project-title="project.title"
      :style-line="scriptPreviewStyleLine"
      :ratio="project.ratio"
      :project-type="project.type"
      :video-prompt-language="projectVideoLang"
      :episode-line="scriptPreviewEpisodeLine"
      :script-type-label="scriptTypeLabel"
      :script-content="scriptContent"
      :reference-line="''"
      @close="closeScriptPreviewModal"
    />
  </div>
</template>

<script>
import { mockProjects } from "../mock/data";
import { userApi } from "../api/user";
import ScriptPreviewModal from "../components/ScriptPreviewModal.vue";
import ImagePreviewLayer from "../components/ImagePreviewLayer.vue";
import {
  HOME_PROJECTS_CACHE_KEY,
  LEGACY_SESSION_KEYS,
  projectStepCacheKey,
  step1StateStorageKey,
  step1TaskIdStorageKey
} from "../constants/projectCache";

/**
 * 项目创作三步（/project/setup）：步骤一剧本与异步任务、步骤二实体与出图任务、步骤三分集列表与缓存。
 * 真源：已登录以 projectDetail + saveProjectDraft 为准；未登录以 sessionStorage 兜底。勿在无关路径重复写 meta。
 */
export default {
  name: "ProjectSetupPage",
  components: {
    ScriptPreviewModal,
    ImagePreviewLayer
  },
  data() {
    const projectId = Number(this.$route.query.id || 0);
    const initialStep = Math.max(1, Math.min(3, Number(this.$route.query.step || 1)));
    const cacheProjects = JSON.parse(sessionStorage.getItem(HOME_PROJECTS_CACHE_KEY) || "[]");
    const allProjects = cacheProjects.length ? cacheProjects : mockProjects;
    const fallback = allProjects[0];
    const found = allProjects.find(item => item.id === projectId) || fallback;
    const project = {
      ...found,
      title: found.title || "",
      ratio: found.ratio || "16:9",
      type: found.type || "剧情演绎",
      videoPromptLanguage: found.videoPromptLanguage === "en" ? "en" : "zh"
    };
    return {
      project,
      videoLangOptions: [
        { label: "中文", value: "zh" },
        { label: "English", value: "en" }
      ],
      ratioOptions: [
        { label: "16:9", value: "16:9" },
        { label: "9:16", value: "9:16" }
      ],
      typeOptions: [
        { label: "剧情演绎", value: "剧情演绎", desc: "以角色对白和剧情为主" },
        { label: "旁白解说", value: "旁白解说", desc: "以旁白叙述为主" }
      ],
      activeStep: initialStep,
      // 注意：activeScriptType 存储英文值，便于后续入库；界面展示使用 label（中文）
      activeScriptType: "upload_script",
      scriptTypes: [
        { value: "upload_script", label: "上传我的剧本" },
        { value: "ai_generate_script_agent", label: "AI生成剧本（agent模式）" }
      ],
      scriptContent: "",
      /** 步骤一剧本原文与后端 AI_STEP1_MAX_SCRIPT_CHARS 对齐 */
      step1ScriptMaxChars: 5000,
      /** 预设集数：智能 / 自定义 */
      episodeCountMode: "smart",
      customEpisodeCount: 5,
      /** 与后端 AI_STEP1_SMART_EPISODE_COUNT / AI_STEP1_CUSTOM_EPISODE_MAX 对齐（创建任务接口可刷新） */
      step1EpisodeSmartDefault: 8,
      step1EpisodeCustomMax: 8,
      steps: [
        { id: 1, label: "1 基础设定" },
        { id: 2, label: "2 场景角色" },
        { id: 3, label: "3 分集脚本" }
      ],
      activeStyle: 1,
      activeRoleId: 1,
      /** 步骤2 左侧资源类型：角色 / 场景 / 素材 */
      resourceSubTab: "role",
      activeSceneId: 1,
      activeMaterialId: 1,
      sceneItems: [],
      materialItems: [],
      /** 步骤一「重新生成剧本」清库进行中 */
      step1Regenerating: false,
      /** 后台 textModels / imageModels 返回的 pointsPerCall，用于展示扣费 */
      textModelRows: [],
      imageModelRows: [],
      styleOptions: [
        { id: 1, name: "韩漫质感", cover: "https://dummyimage.com/120x120/39483b/fff.png&text=1" },
        { id: 2, name: "潮流彩绘", cover: "https://dummyimage.com/120x120/6c5b36/fff.png&text=2" },
        { id: 3, name: "摄影写实", cover: "https://dummyimage.com/120x120/454545/fff.png&text=3" },
        { id: 4, name: "轻漫画风", cover: "https://dummyimage.com/120x120/5c5c6d/fff.png&text=4" },
        { id: 5, name: "古风唯美", cover: "https://dummyimage.com/120x120/6a4f35/fff.png&text=5" },
        { id: 6, name: "古风切镜", cover: "https://dummyimage.com/120x120/4d413b/fff.png&text=6" },
        { id: 7, name: "古风汉韵", cover: "https://dummyimage.com/120x120/543d33/fff.png&text=7" },
        { id: 8, name: "都市简约", cover: "https://dummyimage.com/120x120/384450/fff.png&text=8" }
      ],
      roleTemplates: [
        {
          id: 1,
          name: "林枫",
          age: "二十多岁",
          gender: "男",
          summary: "主角，人物挺拔的造型，三千年轮回的气质。",
          description:
            "身高约180cm，体型修长匀称，五官立体，眉宇沉稳。白衣银腰带的古风装束，整体气质克制冷静，兼具少年感和侠气。",
          avatar: "https://dummyimage.com/80x110/d9d9d9/000.png&text=1",
          preview: ""
        },
        {
          id: 2,
          name: "柳如烟",
          age: "二十岁",
          gender: "女",
          summary: "反派，精致冷艳，外表柔媚但行事果决。",
          description:
            "身形纤细，红黑色古风长裙，眉眼锋利，表情克制，有强烈的目标感和神秘气场。",
          avatar: "https://dummyimage.com/80x110/cd4d67/fff.png&text=2",
          preview: ""
        },
        {
          id: 3,
          name: "柳乘风",
          age: "三十多岁",
          gender: "男",
          summary: "反派，权势滔天，城府极深。",
          description:
            "深色古装，神态沉稳，眼神凌厉，人物整体偏成熟气质，压迫感明显。",
          avatar: "https://dummyimage.com/80x110/4d4364/fff.png&text=3",
          preview: ""
        }
      ],
      sceneTemplates: [
        {
          id: 1,
          name: "仙宫大殿",
          summary: "金碧辉煌的主场景，云海缭绕。",
          description:
            "恢宏的古风宫殿，朱红廊柱、白玉台阶，远景层叠殿宇，氛围庄严。",
          avatar: "https://dummyimage.com/80x110/3d4a5c/fff.png&text=1",
          preview: ""
        },
        {
          id: 2,
          name: "竹林幽径",
          summary: "对战与对话交错的过渡场景。",
          description: "青翠竹林，小径曲折，晨光斑驳，氛围静谧。",
          avatar: "https://dummyimage.com/80x110/2e4a3a/fff.png&text=2",
          preview: ""
        },
        {
          id: 3,
          name: "悬崖云海",
          summary: "终局对决的高张力外景。",
          description: "断崖边缘白云翻涌，远处可见连绵山脉。",
          avatar: "https://dummyimage.com/80x110/4a5a6a/fff.png&text=3",
          preview: ""
        }
      ],
      materialTemplates: [
        {
          id: 1,
          name: "剑气特效",
          summary: "青白剑气划过画面的元素素材。",
          description: "用于战斗镜头的半透剑气与碎石飞溅效果。",
          avatar: "https://dummyimage.com/80x110/5a5a5a/fff.png&text=1",
          preview: ""
        },
        {
          id: 2,
          name: "古韵字幕条",
          summary: "标题与台词条包装。",
          description: "水墨晕染风格的 Lower third 与集数标题框。",
          avatar: "https://dummyimage.com/80x110/6a5a4a/fff.png&text=2",
          preview: ""
        },
        {
          id: 3,
          name: "转场云雾",
          summary: "场次切换的云烟过渡。",
          description: "柔边云雾遮罩，可用于场景切换。",
          avatar: "https://dummyimage.com/80x110/4a4a5a/fff.png&text=3",
          preview: ""
        }
      ],
      storyboardTemplates: [
        {
          id: 1,
          title: "第 1 集：废柴逆袭 苍王降临",
          roles: "2",
          scenes: "1",
          duration: "6 分钟",
          cover: "https://dummyimage.com/108x146/4a3c2f/fff.png&text=1"
        },
        {
          id: 2,
          title: "第 2 集：徒手撼龙爪",
          roles: "3",
          scenes: "1",
          duration: "5 分钟",
          cover: "https://dummyimage.com/108x146/2d3442/fff.png&text=2"
        },
        {
          id: 3,
          title: "第 3 集：潜龙出渊 一拳撼天",
          roles: "4",
          scenes: "1",
          duration: "4 分钟",
          cover: "https://dummyimage.com/108x146/2a232e/fff.png&text=3"
        },
        {
          id: 4,
          title: "第 4 集：战神觉醒 金尊化身",
          roles: "5",
          scenes: "1",
          duration: "4 分钟",
          cover: "https://dummyimage.com/108x146/38455a/fff.png&text=4"
        },
        {
          id: 5,
          title: "第 5 集：斩龙之剑 末世序章",
          roles: "6",
          scenes: "1",
          duration: "4 分钟",
          cover: "https://dummyimage.com/108x146/4a453c/fff.png&text=5"
        }
      ],
      roles: [],
      storyboards: [],
      roleModal: {
        visible: false,
        mode: "create",
        targetId: null
      },
      roleForm: {
        name: "",
        age: "",
        gender: "",
        summary: "",
        description: ""
      },
      storyboardCoverPreview: {
        visible: false,
        src: "",
        alt: "",
        item: null
      },
      entityPreviewDialog: {
        visible: false,
        src: "",
        alt: "",
        entity: null,
        field: "preview"
      },
      storyboardForm: {},
      episodeModal: {
        visible: false,
        title: "",
        content: ""
      },
      scriptPreviewModalVisible: false,
      /** 第一步「开始生成」是否已完成（与缓存同步） */
      step1Generated: false,
      step1Generating: false,
      /** 步骤二：单个生成 / 批量生成加载态 */
      resourceGenerating: false,
      batchGenerating: false,
      /** 步骤二：待轮询的实体预览任务（刷新后从服务端恢复的队列） */
      step2WatchList: [],
      _step2PollTimer: null,
      /** 步骤二：resume / 批量 / 单条轮询串行，避免双通道抢改 step2WatchList */
      _step2Serial: null,
      /** 已保存后禁止切换剧本生成类型 */
      scriptTypeLocked: false,
      /** 与 fa_ai_model 对齐的默认编码（接口失败时兜底） */
      textModelCode: "",
      /** 空串：由 loadDefaultAiModels 按后台启用列表首项填充，避免写死已下线的 z-image 导致与 imageModels 列表对不上 */
      imageModelCode: "",
      videoModelCode: "doubao-seedance-2-0-260128",
      /** 已登录且 projectDetail 成功返回时为 true，用于避免仅用本地 session 覆盖服务端空数据 */
      _hydratedFromServer: false,
      /** 服务端草稿保存防抖 */
      _draftSaveTimer: null,
      /** 步骤一异步任务轮询 */
      _step1TaskId: 0,
      _step1PollTimer: null
    };
  },
  async created() {
    await this.loadSetupDictionary();
    await this.loadDefaultAiModels();
    const token = localStorage.getItem("user_token");
    if (token) {
      await this.hydrateProjectFromApi();
    }
    if (!token || !this._hydratedFromServer) {
      this.hydrateStep1FromCache();
      this.hydrateSteps23FromSessionOnly();
    }
    // 支持从 query.step 直接落位到指定步骤（例如从分镜编辑返回到第三步）
    if (this.activeStep > 1) {
      await this.ensureGeneratedData(this.activeStep);
    }
    this.resumeStep1TaskPollingIfNeeded();
  },
  beforeDestroy() {
    if (this._step1PollTimer) {
      clearTimeout(this._step1PollTimer);
      this._step1PollTimer = null;
    }
    this.clearStep2BackgroundPoll();
    this._step2Serial = Promise.resolve();
  },
  watch: {
    "$route.query.id"() {
      this.hydrateProjectFromApi().then(async () => {
        if (!this._hydratedFromServer) {
          this.hydrateStep1FromCache();
          this.hydrateSteps23FromSessionOnly();
        }
        if (localStorage.getItem("user_token")) {
          await this.loadDefaultAiModels();
        }
        if (this.activeStep > 1) {
          await this.ensureGeneratedData(this.activeStep);
        }
      });
    }
  },
  computed: {
    projectVideoLang() {
      return this.project && this.project.videoPromptLanguage === "en" ? "en" : "zh";
    },
    currentRole() {
      return this.roles.find(item => item.id === this.activeRoleId) || null;
    },
    currentScene() {
      return this.sceneItems.find(item => item.id === this.activeSceneId) || null;
    },
    currentMaterial() {
      return this.materialItems.find(item => item.id === this.activeMaterialId) || null;
    },
    currentResourceList() {
      if (this.resourceSubTab === "role") return this.roles;
      if (this.resourceSubTab === "scene") return this.sceneItems;
      if (this.resourceSubTab === "material") return this.materialItems;
      return this.roles;
    },
    currentResourceEntity() {
      if (this.resourceSubTab === "role") return this.currentRole;
      if (this.resourceSubTab === "scene") return this.currentScene;
      if (this.resourceSubTab === "material") return this.currentMaterial;
      return null;
    },
    roleHasPreviewImage() {
      if (!this.currentRole) return false;
      return !!String(this.currentRole.preview || "").trim();
    },
    entityHasPreviewImage() {
      const e = this.currentResourceEntity;
      if (!e) return false;
      return !!String(e.preview || "").trim();
    },
    resourceDetailTitle() {
      const m = { role: "角色设定", scene: "场景设定", material: "素材设定" };
      return m[this.resourceSubTab] || "设定";
    },
    previewPlaceholderText() {
      const m = {
        role: "暂无预览图，可点击「生成」按钮生成角色预览",
        scene: "暂无预览图，可点击「生成」按钮生成场景预览",
        material: "暂无预览图，可点击「生成」按钮生成素材预览"
      };
      return m[this.resourceSubTab] || "暂无预览图";
    },
    resourcePanelTitle() {
      const m = { role: "我的角色", scene: "我的场景", material: "我的素材" };
      return m[this.resourceSubTab] || "我的角色";
    },
    entityDescriptionPlaceholder() {
      if (this.resourceSubTab === "role") {
        return "请填写角色生图描述：身高、体型、面部特征、发型、眼睛、服装、显著特征；并明确“全身图、白色底图、无任何文字”。";
      }
      if (this.resourceSubTab === "scene") {
        return "请填写场景生图描述：空间结构、材质、色彩、光源、关键陈设；并明确“360°全景图、白色底图、无任何文字、无人”。";
      }
      return "请填写素材生图描述：材质、尺寸、颜色、形状、细节、状态；并明确“完整正面图、白色底图、无任何文字”。";
    },
    isFromEdit() {
      const q = this.$route.query;
      return q.mode === "edit" || q.from === "edit";
    },
    step1Locked() {
      return !!(this.step1Generated || this.scriptTypeLocked);
    },
    scriptPreviewStyleLine() {
      const o = this.styleOptions.find(s => s.id === this.activeStyle);
      const name = o ? o.name : "未选择";
      const def = "3D，CG动画，东方奇幻渲染（示例标签，以所选画面风格为准）";
      const extra = o && String(o.description || "").trim() ? String(o.description).trim() : def;
      return `${name}，${extra}`;
    },
    scriptPreviewEpisodeLine() {
      if (this.episodeCountMode === "custom") {
        return `自定义 ${this.customEpisodeCount} 集（上限 ${this.step1EpisodeCustomMax} 集，与生成服务一致）`;
      }
      return `智能集数（约 ${this.step1EpisodeSmartDefault} 集）`;
    },
    scriptTypeLabel() {
      const t = this.scriptTypes.find(x => x.value === this.activeScriptType);
      return t ? t.label : "—";
    },
    scriptPlaceholder() {
      if (this.activeScriptType === "upload_script") {
        return `请粘贴剧本或小说正文，单次不超过 ${this.step1ScriptMaxChars} 字`;
      }
      if (this.activeScriptType === "ai_generate_script_agent") {
        return "输入一句话或简短梗概即可（如：创作一部玄幻漫剧），由 AI 扩写为完整剧本；也可写得更详细。";
      }
      return "请输入内容";
    },
    /** 与后端 mb_strlen(UTF-8) 对齐：扩展平面字符按单个码点计 */
    scriptContentCharCount() {
      const s = String(this.scriptContent || "");
      return Array.from(s).length;
    },
    scriptContentOverLimit() {
      if (this.step1Locked) return false;
      if (this.activeScriptType !== "upload_script") return false;
      return this.scriptContentCharCount > this.step1ScriptMaxChars;
    },
    step2AllEntities() {
      const a = [];
      if (Array.isArray(this.roles)) a.push(...this.roles);
      if (Array.isArray(this.sceneItems)) a.push(...this.sceneItems);
      if (Array.isArray(this.materialItems)) a.push(...this.materialItems);
      return a.filter(Boolean);
    },
    step2MissingPreviewCount() {
      const list = this.step2AllEntities;
      let n = 0;
      for (const it of list) {
        if (!this.hasPreviewImage(it && it.preview)) n++;
      }
      return n;
    },
    step2Ready() {
      const total = this.step2AllEntities.length;
      if (total <= 0) return false;
      return this.step2MissingPreviewCount === 0;
    },
    /** 当前所选文字模型单次步骤一扣费（含会员折扣，与后台一致） */
    step1PointsValue() {
      const code = String(this.textModelCode || "").trim();
      const rows = Array.isArray(this.textModelRows) ? this.textModelRows : [];
      const hit = rows.find(x => String(x.code || "") === code);
      const p = hit && typeof hit.pointsPerCall === "number" ? hit.pointsPerCall : 0;
      return Number.isFinite(p) && p > 0 ? p : 0;
    },
    step1PointsLabel() {
      return this.step1PointsValue > 0 ? `${this.step1PointsValue} 积分` : "未配置扣费";
    },
    step1GenerateDisabled() {
      if (this.step1Generating) return true;
      if (this.activeScriptType === "upload_script" && this.scriptContentOverLimit) return true;
      if (this.step1PointsValue <= 0) return true;
      return false;
    },
    step2SinglePointsValue() {
      const code = String(this.imageModelCode || "").trim();
      const rows = Array.isArray(this.imageModelRows) ? this.imageModelRows : [];
      let hit = rows.find(x => String(x.code || "") === code);
      // 与后端 resolveUsableImageModel 一致：当前 code 不在启用列表时按 weigh/id 首项计费展示
      if (!hit && rows.length) {
        hit = rows[0];
      }
      const p = hit && typeof hit.pointsPerCall === "number" ? hit.pointsPerCall : 0;
      return Number.isFinite(p) && p > 0 ? p : 0;
    },
    step2SinglePointsLabel() {
      return this.step2SinglePointsValue > 0 ? `${this.step2SinglePointsValue} 积分/张` : "未配置扣费";
    },
    batchImagePointsSummary() {
      const p = this.step2SinglePointsValue;
      if (p <= 0) return "未配置扣费";
      const tab = this.resourceSubTab;
      const list = tab === "role" ? this.roles : tab === "scene" ? this.sceneItems : this.materialItems;
      if (!Array.isArray(list) || !list.length) return `${p} 积分/张`;
      const n = list.filter(it => it && it.id && !this.hasPreviewImage(it.preview)).length;
      if (n <= 0) return `约 ${p} 积分/张`;
      //return `约 ${n * p} 积分（${n} 张×${p}）`;
      return `约 ${n * p} 积分`;
    },
    step2ImageWorkLocked() {
      return (
        this.batchGenerating ||
        this.resourceGenerating ||
        (Array.isArray(this.step2WatchList) && this.step2WatchList.length > 0)
      );
    },
    step2CurrentEntityBusyLabel() {
      const e = this.currentResourceEntity;
      const inQueue =
        e &&
        e.id &&
        this.step2WatchList.some(j => Number(j.entityId) === Number(e.id));
      if (this.resourceGenerating || inQueue) {
        return this.entityHasPreviewImage ? "重新生成中…" : "生成中…";
      }
      return this.entityHasPreviewImage ? "重新生成" : "生成";
    },
    step2QueueHintText() {
      const n = Array.isArray(this.step2WatchList) ? this.step2WatchList.length : 0;
      if (!n) return "";
      const names = this.step2WatchList
        .map(j => (j.entityName && String(j.entityName).trim()) || `#${j.entityId}`)
        .slice(0, 5);
      const tail = n > 5 ? `等 ${n} 项` : `共 ${n} 项`;
      return `预览图队列进行中：${names.join("、")}（${tail}）。刷新不会中断任务，请保持 Worker 或 taskListener 在跑。`;
    }
  },
  methods: {
    onImgError(item, field) {
      if (!item || !field) return;
      try {
        // 将失败图片置空，使 isPlaceholderAsset 命中并回退到占位块
        item[field] = "";
      } catch (e) {
        /* ignore */
      }
    },
    windowAlert(message) {
      window.alert(message);
    },
    notifyPointsChanged() {
      if (this.$root && typeof this.$root.$emit === "function") {
        this.$root.$emit("points-changed");
      }
    },
    async loadSetupDictionary() {
      try {
        const d = await userApi.setupDictionary();
        if (!d || typeof d !== "object") return;
        if (Array.isArray(d.aspectRatio) && d.aspectRatio.length) {
          this.ratioOptions = d.aspectRatio.map(x => ({
            label: x.label || x.value,
            value: x.value
          }));
        }
        if (Array.isArray(d.projectType) && d.projectType.length) {
          this.typeOptions = d.projectType.map(x => ({
            label: x.label || x.value,
            value: x.value,
            desc: x.desc || ""
          }));
        }
        if (Array.isArray(d.visualStyle) && d.visualStyle.length) {
          this.styleOptions = d.visualStyle
            .map(x => ({
              id: Number(x.id),
              name: x.name || "",
              cover: x.cover || "",
              description: x.description || ""
            }))
            .filter(s => Number.isFinite(s.id) && s.id > 0);
          if (this.styleOptions.length) {
            const ids = new Set(this.styleOptions.map(s => s.id));
            if (!ids.has(Number(this.activeStyle))) {
              this.activeStyle = Number(this.styleOptions[0].id);
            }
          }
        }
        const rv = String(this.project.ratio || "");
        if (this.ratioOptions.length && !this.ratioOptions.some(o => o.value === rv)) {
          this.project.ratio = this.ratioOptions[0].value;
        }
        const tv = String(this.project.type || "");
        if (this.typeOptions.length && !this.typeOptions.some(o => o.value === tv)) {
          this.project.type = this.typeOptions[0].value;
        }
      } catch (e) {
        /* 保留页面内置默认项 */
      }
    },
    setActiveStyle(id) {
      this.activeStyle = id;
      this.persistStep1State();
    },
    setProjectRatio(value) {
      this.project.ratio = value;
      this.persistProjectToCache();
    },
    setProjectType(value) {
      this.project.type = value;
      this.persistProjectToCache();
    },
    async setProjectVideoLang(value) {
      const v = value === "en" ? "en" : "zh";
      this.$set(this.project, "videoPromptLanguage", v);
      this.persistProjectToCache();
      const id = this.project && this.project.id;
      if (!id || !localStorage.getItem("user_token")) return;
      try {
        await userApi.updateProject({
          id,
          title: this.project.title || "",
          ratio: this.project.ratio || "16:9",
          type: this.project.type || "剧情演绎",
          cover: this.project.cover || "",
          videoPromptLanguage: v
        });
      } catch (e) {
        /* 离线或接口失败：本地已切换，刷新后可能回退 */
      }
    },
    clampCustomEpisodeCount() {
      let n = Number(this.customEpisodeCount);
      if (Number.isNaN(n) || n < 1) n = 1;
      const cap = Math.max(1, Math.min(200, Number(this.step1EpisodeCustomMax) || 8));
      if (n > cap) n = cap;
      this.customEpisodeCount = n;
    },
    applyStep1EpisodeLimitsFromApi(res) {
      if (!res || typeof res !== "object") return;
      const lim = res.episodeCountLimits;
      if (lim && typeof lim === "object") {
        const mx = Number(lim.customMax);
        const sm = Number(lim.smartDefault);
        if (Number.isFinite(mx) && mx >= 1) this.step1EpisodeCustomMax = Math.min(200, Math.floor(mx));
        if (Number.isFinite(sm) && sm >= 1) this.step1EpisodeSmartDefault = Math.min(200, Math.floor(sm));
      }
    },
    hydrateStep1FromCache() {
      const id = this.project && this.project.id;
      if (!id) return;
      const raw = sessionStorage.getItem(step1StateStorageKey(id));
      if (!raw) return;
      try {
        const s = JSON.parse(raw);
        if (s.activeScriptType) this.activeScriptType = s.activeScriptType;
        if (typeof s.scriptContent === "string") this.scriptContent = s.scriptContent;
        if (s.episodeCountMode === "smart" || s.episodeCountMode === "custom") {
          this.episodeCountMode = s.episodeCountMode;
        }
        if (typeof s.customEpisodeCount === "number") this.customEpisodeCount = s.customEpisodeCount;
        this.clampCustomEpisodeCount();
        if (typeof s.activeStyle === "number") this.activeStyle = s.activeStyle;
        if (typeof s.scriptSaved === "boolean") this.scriptTypeLocked = s.scriptSaved;
        else if (typeof s.scriptSaved === "number" || typeof s.scriptSaved === "string") {
          this.scriptTypeLocked = Number(s.scriptSaved) === 1;
        } else this.scriptTypeLocked = !!s.scriptSaved;
        if (typeof s.step1Generated === "boolean") this.step1Generated = s.step1Generated;
        else if (typeof s.step1Generated === "number" || typeof s.step1Generated === "string") {
          this.step1Generated = Number(s.step1Generated) === 1;
        } else this.step1Generated = !!s.step1Generated;
      } catch (e) {
        /* ignore */
      }
    },
    persistStep1State({ scriptSaved } = {}) {
      const id = this.project && this.project.id;
      if (!id) return;
      const saved = scriptSaved !== undefined ? scriptSaved : this.scriptTypeLocked;
      const payload = {
        scriptSaved: saved,
        step1Generated: this.step1Generated,
        activeScriptType: this.activeScriptType,
        scriptContent: this.scriptContent,
        episodeCountMode: this.episodeCountMode,
        customEpisodeCount: this.customEpisodeCount,
        activeStyle: this.activeStyle
      };
      sessionStorage.setItem(step1StateStorageKey(id), JSON.stringify(payload));
      this.scheduleSaveProjectDraft();
    },
    step1TaskKey() {
      return step1TaskIdStorageKey(this.project && this.project.id);
    },
    setStep1TaskId(taskId) {
      const id = Number(taskId || 0);
      this._step1TaskId = id;
      if (id > 0) sessionStorage.setItem(this.step1TaskKey(), String(id));
      else sessionStorage.removeItem(this.step1TaskKey());
    },
    readStep1TaskIdFromSession() {
      const raw = sessionStorage.getItem(this.step1TaskKey());
      const id = Number(raw || 0);
      return Number.isFinite(id) ? id : 0;
    },
    resumeStep1TaskPollingIfNeeded() {
      if (!localStorage.getItem("user_token")) return;
      if (this.step1Generated) return;
      if (this.step1Generating) return;
      const taskId = this.readStep1TaskIdFromSession();
      if (taskId > 0) {
        this.startStep1TaskPolling(taskId, 5000);
      }
    },
    buildStep1MetaForApi() {
      return {
        activeScriptType: this.activeScriptType,
        scriptContent: this.scriptContent,
        episodeCountMode: this.episodeCountMode,
        customEpisodeCount: this.customEpisodeCount,
        activeStyle: this.activeStyle,
        step1Generated: this.step1Generated,
        scriptSaved: this.scriptTypeLocked
      };
    },
    /** 完整 meta_json（三步 + 模型编码），与后端 normalizeProjectMeta 对齐 */
    buildFullProjectMeta() {
      return {
        version: 1,
        step1: this.buildStep1MetaForApi(),
        step2: {
          roles: this.roles,
          scenes: this.sceneItems,
          materials: this.materialItems
        },
        step3: {
          storyboards: this.storyboards
        },
        models: {
          textModelCode: this.textModelCode,
          imageModelCode: this.imageModelCode,
          videoModelCode: this.videoModelCode
        },
        lastActiveStep: this.activeStep
      };
    },
    ssKey(kind) {
      return projectStepCacheKey(kind, this.project && this.project.id);
    },
    readSessionList(kind, legacyKey) {
      let raw = sessionStorage.getItem(this.ssKey(kind));
      if (!raw && legacyKey) {
        raw = sessionStorage.getItem(legacyKey);
      }
      try {
        const a = JSON.parse(raw || "[]");
        return Array.isArray(a) ? a : [];
      } catch (e) {
        return [];
      }
    },
    hydrateSteps23FromSessionOnly() {
      const r = this.readSessionList("roles", LEGACY_SESSION_KEYS.roles);
      const s = this.readSessionList("scenes", LEGACY_SESSION_KEYS.scenes);
      const m = this.readSessionList("materials", LEGACY_SESSION_KEYS.materials);
      const sb = this.readSessionList("storyboards", LEGACY_SESSION_KEYS.storyboards);
      if (r.length) {
        this.roles = r;
        this.activeRoleId = this.roles[0] ? this.roles[0].id : null;
      }
      if (s.length) {
        this.sceneItems = s;
        this.activeSceneId = this.sceneItems[0] ? this.sceneItems[0].id : null;
      }
      if (m.length) {
        this.materialItems = m;
        this.activeMaterialId = this.materialItems[0] ? this.materialItems[0].id : null;
      }
      if (sb.length) {
        this.storyboards = sb;
      }
    },
    async loadDefaultAiModels() {
      try {
        const [t, i] = await Promise.all([
          userApi.textModels().catch(() => []),
          userApi.imageModels().catch(() => [])
        ]);
        this.textModelRows = Array.isArray(t) ? t : [];
        this.imageModelRows = Array.isArray(i) ? i : [];
        const curText = String(this.textModelCode || "").trim();
        const curImg = String(this.imageModelCode || "").trim();
        const textCodes = new Set(
          this.textModelRows.map(r => String(r.code || "").trim()).filter(Boolean)
        );
        const imgCodes = new Set(
          this.imageModelRows.map(r => String(r.code || "").trim()).filter(Boolean)
        );
        if (this.textModelRows.length && this.textModelRows[0].code && (!curText || !textCodes.has(curText))) {
          this.textModelCode = this.textModelRows[0].code;
        }
        if (this.imageModelRows.length && this.imageModelRows[0].code && (!curImg || !imgCodes.has(curImg))) {
          this.imageModelCode = this.imageModelRows[0].code;
        }
      } catch (e) {
        /* 使用 data 默认值 */
      }
    },
    syncSessionCachesFromState() {
      const id = this.project && this.project.id;
      if (!id) return;
      const flush = () => {
        try {
          sessionStorage.setItem(this.ssKey("roles"), JSON.stringify(this.roles));
          sessionStorage.setItem(this.ssKey("scenes"), JSON.stringify(this.sceneItems));
          sessionStorage.setItem(this.ssKey("materials"), JSON.stringify(this.materialItems));
          sessionStorage.setItem(this.ssKey("storyboards"), JSON.stringify(this.storyboards));
          const payload = {
            scriptSaved: this.scriptTypeLocked,
            step1Generated: this.step1Generated,
            activeScriptType: this.activeScriptType,
            scriptContent: this.scriptContent,
            episodeCountMode: this.episodeCountMode,
            customEpisodeCount: this.customEpisodeCount,
            activeStyle: this.activeStyle
          };
          sessionStorage.setItem(step1StateStorageKey(id), JSON.stringify(payload));
        } catch (e) {
          /* ignore quota / private mode */
        }
      };
      if (typeof requestIdleCallback === "function") {
        requestIdleCallback(() => flush(), { timeout: 900 });
      } else {
        setTimeout(flush, 0);
      }
    },
    /** projectDetail 顶层 storyboards 可能不含 shots（与 episodes 拆分传输），合并后再写入草稿 */
    mergeStoryboardsWithEpisodesShots(storyboards, episodes) {
      if (!Array.isArray(storyboards)) return [];
      const eps = Array.isArray(episodes) ? episodes : [];
      const byEp = new Map(eps.map(ep => [Number((ep && ep.id) || 0), ep]));
      return storyboards.map(sb => {
        const sid = Number((sb && sb.id) || 0);
        if (Array.isArray(sb.shots) && sb.shots.length) return sb;
        const ep = byEp.get(sid);
        const shots = ep && Array.isArray(ep.shots) ? ep.shots : [];
        return { ...sb, shots };
      });
    },
    scheduleSaveProjectDraft() {
      const id = this.project && this.project.id;
      if (!id || !localStorage.getItem("user_token")) return;
      clearTimeout(this._draftSaveTimer);
      this._draftSaveTimer = setTimeout(() => this.flushProjectDraftToApi(), 1500);
    },
    async flushProjectDraftToApi() {
      const id = this.project && this.project.id;
      if (!id || !localStorage.getItem("user_token")) return;
      try {
        await userApi.saveProjectDraft({
          id,
          metaJson: JSON.stringify(this.buildFullProjectMeta())
        });
      } catch (e) {
        /* 离线或未登录 */
      }
    },
    mergeProjectIntoHomeCache() {
      const id = this.project && this.project.id;
      if (!id) return;
      let list = [];
      try {
        list = JSON.parse(sessionStorage.getItem(HOME_PROJECTS_CACHE_KEY) || "[]");
      } catch (e) {
        list = [];
      }
      const idx = list.findIndex(p => p.id === id);
      const entry = {
        id,
        title: this.project.title || "",
        ratio: this.project.ratio,
        type: this.project.type,
        videoPromptLanguage: this.project.videoPromptLanguage === "en" ? "en" : "zh",
        cover: this.project.cover || "",
        updateText: idx !== -1 && list[idx].updateText ? list[idx].updateText : ""
      };
      if (idx !== -1) list[idx] = { ...list[idx], ...entry };
      else list.unshift(entry);
      sessionStorage.setItem(HOME_PROJECTS_CACHE_KEY, JSON.stringify(list));
    },
    async hydrateProjectFromApi() {
      const pid = Number(this.$route.query.id || 0);
      if (!pid || !localStorage.getItem("user_token")) {
        this._hydratedFromServer = false;
        return;
      }
      this.clearStep2BackgroundPoll();
      this._step2Serial = Promise.resolve();
      try {
        const row = await userApi.projectDetail(pid);
        if (!row || typeof row !== "object") {
          this._hydratedFromServer = false;
          return;
        }
        this._hydratedFromServer = true;
        this.project.id = Number(row.id) || pid;
        if (row.title) this.project.title = row.title;
        if (row.ratio) this.project.ratio = row.ratio;
        if (row.type) this.project.type = row.type;
        this.$set(
          this.project,
          "videoPromptLanguage",
          row.videoPromptLanguage === "en" ? "en" : "zh"
        );
        if (row.cover !== undefined && row.cover !== null) this.$set(this.project, "cover", row.cover);
        const vsc = Number(row.visualStyleCode);
        if (Number.isFinite(vsc) && vsc > 0) this.activeStyle = vsc;
        const m = row.meta && typeof row.meta === "object" ? row.meta : {};
        const s1 = m.step1 && typeof m.step1 === "object" ? m.step1 : m;
        if (s1.activeScriptType) this.activeScriptType = s1.activeScriptType;
        if (typeof row.scriptContent === "string") {
          this.scriptContent = row.scriptContent;
        } else if (typeof s1.scriptContent === "string") {
          this.scriptContent = s1.scriptContent;
        }
        if (s1.episodeCountMode === "smart" || s1.episodeCountMode === "custom") {
          this.episodeCountMode = s1.episodeCountMode;
        }
        if (typeof s1.customEpisodeCount === "number") this.customEpisodeCount = s1.customEpisodeCount;
        this.clampCustomEpisodeCount();
        if (typeof s1.activeStyle === "number" && s1.activeStyle > 0) this.activeStyle = s1.activeStyle;
        if (typeof s1.step1Generated === "boolean") {
          this.step1Generated = s1.step1Generated;
        } else if (typeof s1.step1Generated === "number" || typeof s1.step1Generated === "string") {
          this.step1Generated = Number(s1.step1Generated) === 1;
        }
        if (typeof s1.scriptSaved === "boolean") {
          this.scriptTypeLocked = s1.scriptSaved;
        } else if (typeof s1.scriptSaved === "number" || typeof s1.scriptSaved === "string") {
          this.scriptTypeLocked = Number(s1.scriptSaved) === 1;
        }
        // 兼容历史数据：若步骤二/三已有产物，说明步骤一已生成，应自动锁定第一步编辑
        if (!this.step1Generated && (Array.isArray(row.roles) && row.roles.length || Array.isArray(row.storyboards) && row.storyboards.length)) {
          this.step1Generated = true;
        }
        if (!this.scriptTypeLocked && this.step1Generated) {
          this.scriptTypeLocked = true;
        }
        if (Array.isArray(row.roles)) {
          this.roles = row.roles;
        } else if (m.step2 && Array.isArray(m.step2.roles)) {
          this.roles = m.step2.roles;
        }
        if (Array.isArray(row.scenes)) {
          this.sceneItems = row.scenes;
        } else if (m.step2 && Array.isArray(m.step2.scenes)) {
          this.sceneItems = m.step2.scenes;
        }
        if (Array.isArray(row.materials)) {
          this.materialItems = row.materials;
        } else if (m.step2 && Array.isArray(m.step2.materials)) {
          this.materialItems = m.step2.materials;
        }
        if (Array.isArray(row.storyboards)) {
          this.storyboards = this.mergeStoryboardsWithEpisodesShots(row.storyboards, row.episodes);
        } else if (m.step3 && Array.isArray(m.step3.storyboards)) {
          this.storyboards = this.mergeStoryboardsWithEpisodesShots(m.step3.storyboards, row.episodes);
        }
        if (row.models && typeof row.models === "object") {
          if (row.models.textModelCode) this.textModelCode = row.models.textModelCode;
          if (row.models.imageModelCode) this.imageModelCode = row.models.imageModelCode;
          if (row.models.videoModelCode) this.videoModelCode = row.models.videoModelCode;
        } else if (m.models && typeof m.models === "object") {
          if (m.models.textModelCode) this.textModelCode = m.models.textModelCode;
          if (m.models.imageModelCode) this.imageModelCode = m.models.imageModelCode;
          if (m.models.videoModelCode) this.videoModelCode = m.models.videoModelCode;
        }
        if (this.roles[0]) this.activeRoleId = this.roles[0].id;
        else this.activeRoleId = null;
        if (this.sceneItems[0]) this.activeSceneId = this.sceneItems[0].id;
        else this.activeSceneId = null;
        if (this.materialItems[0]) this.activeMaterialId = this.materialItems[0].id;
        else this.activeMaterialId = null;
        if (localStorage.getItem("user_token")) {
          await this.loadDefaultAiModels();
          await this.resumeStep2ImageTasksFromServer();
        }
        this.syncSessionCachesFromState();
        this.mergeProjectIntoHomeCache();
      } catch (e) {
        this._hydratedFromServer = false;
      }
    },
    openScriptPreviewModal() {
      this.scriptPreviewModalVisible = true;
    },
    closeScriptPreviewModal() {
      this.scriptPreviewModalVisible = false;
    },
    clearProjectStep23SessionCaches() {
      const id = this.project && this.project.id;
      if (!id) return;
      try {
        sessionStorage.removeItem(this.ssKey("roles"));
        sessionStorage.removeItem(this.ssKey("scenes"));
        sessionStorage.removeItem(this.ssKey("materials"));
        sessionStorage.removeItem(this.ssKey("storyboards"));
        sessionStorage.removeItem(step1TaskIdStorageKey(id));
      } catch (e) {
        /* ignore */
      }
    },
    async handleStep1RegenerateReset() {
      if (this.step1Regenerating || this.step1Generating) return;
      const id = this.project && this.project.id;
      if (!id) return;
      if (!localStorage.getItem("user_token")) {
        this.windowAlert("请先登录后再操作");
        return;
      }
      if (
        !window.confirm(
          "将清空本项目已落库的步骤一～三数据（角色/场景/素材、分集、分镜及关联绑定），并取消进行中的生成任务。剧本正文会尽量保留。确定继续？"
        )
      ) {
        return;
      }
      this.step1Regenerating = true;
      try {
        const res = await userApi.resetProjectForStep1Regenerate({ id });
        if (res && typeof res.scriptContentKept === "string") {
          this.scriptContent = res.scriptContentKept;
        }
        this.step1Generated = false;
        this.scriptTypeLocked = false;
        this.roles = [];
        this.sceneItems = [];
        this.materialItems = [];
        this.storyboards = [];
        this.activeRoleId = null;
        this.activeSceneId = null;
        this.activeMaterialId = null;
        this.setStep1TaskId(0);
        this.clearProjectStep23SessionCaches();
        this.persistStep1State({ scriptSaved: false });
        await this.hydrateProjectFromApi();
        this.goToStep(1);
        this.notifyPointsChanged();
        this.windowAlert("已清空，可修改剧本后再次「开始生成」。");
      } catch (e) {
        this.windowAlert(e && e.message ? e.message : "清空失败");
      } finally {
        this.step1Regenerating = false;
      }
    },
    onScriptTypeClick(value) {
      if (this.scriptTypeLocked) {
        this.windowAlert("已生成的剧本设定后不支持切换生成类型，请重新创建项目。");
        return;
      }
      this.activeScriptType = value;
    },
    async handleStep1Generate() {
      if (this.step1Generating) return;
      if (this.step1PointsValue <= 0) {
        this.windowAlert(
          "当前文字模型未配置步骤一扣费，请在后端 fa_ai_points_config 中维护 scene_code=step1_generate 的积分规则。"
        );
        return;
      }
      if (!String(this.project.title || "").trim()) {
        this.windowAlert("请先填写项目名称");
        return;
      }
      if (!String(this.scriptContent || "").trim()) {
        this.windowAlert("请先填写剧本内容");
        return;
      }
      if (this.activeScriptType === "upload_script" && this.scriptContentOverLimit) {
        this.windowAlert(
          `当前模型单次仅支持 ${this.step1ScriptMaxChars} 字剧本，为保证稳定性和分镜质量，请分段创作。`
        );
        return;
      }
      this.step1Generating = true;
      try {
        if (localStorage.getItem("user_token")) {
          const createRes = await userApi.createGenerateProjectStep1Task({
            id: this.project.id,
            scriptContent: this.scriptContent,
            activeScriptType: this.activeScriptType,
            episodeCountMode: this.episodeCountMode,
            customEpisodeCount: this.customEpisodeCount,
            activeStyle: this.activeStyle,
            projectType: this.project.type,
            textModelCode: this.textModelCode,
            imageModelCode: this.imageModelCode,
            videoPromptLanguage: this.projectVideoLang
          });
          this.applyStep1EpisodeLimitsFromApi(createRes);
          if (
            createRes.normalizedEpisodeCountMode === "smart" ||
            createRes.normalizedEpisodeCountMode === "custom"
          ) {
            this.episodeCountMode = createRes.normalizedEpisodeCountMode;
          }
          if (
            typeof createRes.normalizedCustomEpisodeCount === "number" &&
            Number.isFinite(createRes.normalizedCustomEpisodeCount)
          ) {
            this.customEpisodeCount = createRes.normalizedCustomEpisodeCount;
          }
          this.clampCustomEpisodeCount();
          const taskId = Number(createRes.taskId || 0);
          if (!taskId) {
            throw new Error("任务创建失败");
          }
          this.setStep1TaskId(taskId);
          this.startStep1TaskPolling(taskId, Number(createRes.pollIntervalMs || 10000));
          let tip = "已提交生成任务，正在后台处理中。可继续操作，刷新后也会自动续轮询。";
          if (createRes.step1AgentHint) {
            tip = `${createRes.step1AgentHint}\n\n${tip}`;
          }
          this.windowAlert(tip);
        } else {
          await new Promise(resolve => setTimeout(resolve, 10000));
          this.step1Generated = true;
          this.scriptTypeLocked = true;
          this.step1Generating = false;
          return;
        }
      } catch (e) {
        this.step1Generating = false;
        this.windowAlert(e && e.message ? e.message : "生成失败");
      }
    },
    startStep1TaskPolling(taskId, intervalMs = 10000) {
      if (!taskId) return;
      if (this._step1PollTimer) {
        clearTimeout(this._step1PollTimer);
        this._step1PollTimer = null;
      }
      this.step1Generating = true;
      this.pollStep1TaskUntilDone(taskId, intervalMs);
    },
    async pollStep1TaskUntilDone(taskId, intervalMs = 10000) {
      const start = Date.now();
      const timeoutMs = 10 * 60 * 1000;
      let waitMs = Math.max(1000, intervalMs);
      // 轮询到任务成功/失败；running 时仅等待，不触发额外并发请求
      for (;;) {
        let st = null;
        try {
          st = await userApi.getGenerateProjectStep1TaskStatus({ taskId });
        } catch (e) {
          // 轮询请求偶发失败时不终止任务，稍后重试，避免前端直接“卡死/失败”
          await new Promise(resolve => {
            this._step1PollTimer = setTimeout(resolve, waitMs);
          });
          this._step1PollTimer = null;
          waitMs = Math.min(4000, waitMs + 500);
          continue;
        }
        const status = String(st.status || "");
        if (status === "success") {
          this.setStep1TaskId(0);
          const r = st.result || {};
          if (typeof r.scriptContent === "string") this.scriptContent = r.scriptContent;
          this.step1Generated = r.step1Generated !== undefined ? !!r.step1Generated : true;
          this.scriptTypeLocked = r.scriptSaved !== undefined ? !!r.scriptSaved : true;
          this.step1Generating = false;
          this.persistStep1State({ scriptSaved: true });
          clearTimeout(this._draftSaveTimer);
          await this.flushProjectDraftToApi();
          if (localStorage.getItem("user_token")) {
            await this.hydrateProjectFromApi();
          }
          this.notifyPointsChanged();
          await this.goToStep(2);
          return;
        }
        if (status === "failed") {
          this.setStep1TaskId(0);
          this.step1Generating = false;
          this.windowAlert(st.errorMessage || st.message || "步骤一生成失败");
          return;
        }
        if (Date.now() - start > timeoutMs) {
          this.step1Generating = false;
          this.windowAlert(
            "本页已连续等待超过 10 分钟。后台任务可能仍在执行；可刷新页面继续自动轮询，或稍后在项目里查看是否已生成完成。"
          );
          return;
        }
        await new Promise(resolve => {
          this._step1PollTimer = setTimeout(resolve, waitMs);
        });
        this._step1PollTimer = null;
        waitMs = Math.min(3000, waitMs + 200);
      }
    },
    persistProjectTitle() {
      if (!this.project.title) {
        this.windowAlert("项目名称不能为空");
        const fallbackTitle =
          (this.$route.query.title && String(this.$route.query.title)) || "项目";
        this.project.title = fallbackTitle;
        return;
      }
      this.persistProjectToCache();
    },
    persistProjectToCache() {
      const id = this.project.id;
      if (!id) return;
      let list = [];
      try {
        list = JSON.parse(sessionStorage.getItem(HOME_PROJECTS_CACHE_KEY) || "[]");
      } catch (e) {
        list = [];
      }
      const idx = list.findIndex(p => p.id === id);
      if (idx !== -1) {
        list[idx] = {
          ...list[idx],
          title: this.project.title,
          ratio: this.project.ratio,
          type: this.project.type
        };
      } else {
        list.push({
          ...this.project
        });
      }
      sessionStorage.setItem(HOME_PROJECTS_CACHE_KEY, JSON.stringify(list));
      this.$router
        .replace({
          query: {
            ...this.$route.query,
            title: this.project.title || undefined
          }
        })
        .catch(err => {
          if (err && err.name === "NavigationDuplicated") return;
          throw err;
        });
    },
    async goToStep(step) {
      if (step < 1 || step > 3) return;
      if (step > 1 && this.activeStep === 1 && !this.step1Locked) {
        this.windowAlert("请先点击「开始生成」并完成生成后再进入下一步。");
        return;
      }
      if (step === 3 && this.activeStep === 2 && !this.step2Ready) {
        const missing = this.step2MissingPreviewCount;
        this.windowAlert(missing > 0 ? `请先为所有角色/场景/素材生成或上传图片（还差 ${missing} 个）` : "请先生成或上传图片");
        return;
      }
      if (step === 2 && this.activeStep === 1) {
        this.persistStep1State({ scriptSaved: this.scriptTypeLocked });
        clearTimeout(this._draftSaveTimer);
        await this.flushProjectDraftToApi();
      }
      if (step > 1) {
        // 已登录时真源在服务端：须再次 projectDetail，避免仅读空 session 导致步骤二/三空白（刷新才恢复）
        await this.ensureGeneratedData(step);
      }
      const currentStepInQuery = Number(this.$route.query.step || 1);
      this.activeStep = step;
      // 重复点击当前步骤时，不再触发重复导航（VueRouter 会抛 NavigationDuplicated）。
      if (currentStepInQuery === step) return;
      this.$router
        .replace({
          query: {
            ...this.$route.query,
            step: String(step)
          }
        })
        .catch(err => {
          if (err && err.name === "NavigationDuplicated") return;
          throw err;
        });
    },
    /**
     * @param {number} [targetStep] 即将进入的步骤（goToStep 在写入 activeStep 之前传入，用于判断是否要拉 projectDetail）
     */
    async ensureGeneratedData(targetStep) {
      const stepRef = Math.max(1, Math.min(3, Number(targetStep) || this.activeStep || 1));
      const hasToken = !!localStorage.getItem("user_token");
      if (!this.scriptContent) {
        const cachedStoryboards = this.readSessionList("storyboards", LEGACY_SESSION_KEYS.storyboards);
        const cachedRoles = this.readSessionList("roles", LEGACY_SESSION_KEYS.roles);
        const cachedScenes = this.readSessionList("scenes", LEGACY_SESSION_KEYS.scenes);
        const cachedMaterials = this.readSessionList("materials", LEGACY_SESSION_KEYS.materials);
        if (cachedStoryboards.length) {
          this.storyboards = cachedStoryboards;
        } else {
          this.storyboards = [];
        }
        if (cachedRoles.length) {
          this.roles = cachedRoles;
          this.activeRoleId = this.roles[0] ? this.roles[0].id : null;
        } else {
          this.roles = [];
          this.activeRoleId = null;
        }
        if (cachedScenes.length) {
          this.sceneItems = cachedScenes;
          this.activeSceneId = this.sceneItems[0] ? this.sceneItems[0].id : null;
        } else {
          this.sceneItems = [];
          this.activeSceneId = null;
        }
        if (cachedMaterials.length) {
          this.materialItems = cachedMaterials;
          this.activeMaterialId = this.materialItems[0] ? this.materialItems[0].id : null;
        } else {
          this.materialItems = [];
          this.activeMaterialId = null;
        }
        return;
      }
      if (hasToken) {
        const cachedRoles = this.readSessionList("roles", LEGACY_SESSION_KEYS.roles);
        const cachedScenes = this.readSessionList("scenes", LEGACY_SESSION_KEYS.scenes);
        const cachedMaterials = this.readSessionList("materials", LEGACY_SESSION_KEYS.materials);
        const cachedStoryboards = this.readSessionList("storyboards", LEGACY_SESSION_KEYS.storyboards);
        if (!this.roles.length && cachedRoles.length) {
          this.roles = cachedRoles;
          this.activeRoleId = this.roles[0] ? this.roles[0].id : null;
        }
        if (!this.sceneItems.length && cachedScenes.length) {
          this.sceneItems = cachedScenes;
          this.activeSceneId = this.sceneItems[0] ? this.sceneItems[0].id : null;
        }
        if (!this.materialItems.length && cachedMaterials.length) {
          this.materialItems = cachedMaterials;
          this.activeMaterialId = this.materialItems[0] ? this.materialItems[0].id : null;
        }
        if (!this.storyboards.length && cachedStoryboards.length) {
          this.storyboards = cachedStoryboards;
        }
        const pid = Number(this.$route.query.id || 0);
        if (pid) {
          const emptyResources =
            (!Array.isArray(this.roles) || !this.roles.length) &&
            (!Array.isArray(this.sceneItems) || !this.sceneItems.length) &&
            (!Array.isArray(this.materialItems) || !this.materialItems.length);
          const emptyStoryboards = !Array.isArray(this.storyboards) || !this.storyboards.length;
          const needServer =
            (stepRef >= 2 || this.step1Generated) && (emptyResources || emptyStoryboards);
          if (needServer) {
            await this.hydrateProjectFromApi();
          }
        }
        return;
      }
      if (!this.roles.length) {
        const cachedRoles = this.readSessionList("roles", LEGACY_SESSION_KEYS.roles);
        this.roles = cachedRoles.length ? cachedRoles : this.roleTemplates.map(item => ({ ...item }));
        this.activeRoleId = this.roles[0] ? this.roles[0].id : null;
      }
      if (!this.sceneItems.length) {
        const cachedScenes = this.readSessionList("scenes", LEGACY_SESSION_KEYS.scenes);
        this.sceneItems = cachedScenes.length ? cachedScenes : this.sceneTemplates.map(item => ({ ...item }));
        this.activeSceneId = this.sceneItems[0] ? this.sceneItems[0].id : null;
      }
      if (!this.materialItems.length) {
        const cachedMaterials = this.readSessionList("materials", LEGACY_SESSION_KEYS.materials);
        this.materialItems = cachedMaterials.length ? cachedMaterials : this.materialTemplates.map(item => ({ ...item }));
        this.activeMaterialId = this.materialItems[0] ? this.materialItems[0].id : null;
      }
      if (!this.storyboards.length) {
        this.storyboards = this.storyboardTemplates.map(item => ({ ...item }));
        this.persistStoryboardsCache();
      }
    },
    persistResourceCaches() {
      sessionStorage.setItem(this.ssKey("roles"), JSON.stringify(this.roles));
      sessionStorage.setItem(this.ssKey("scenes"), JSON.stringify(this.sceneItems));
      sessionStorage.setItem(this.ssKey("materials"), JSON.stringify(this.materialItems));
      this.scheduleSaveProjectDraft();
    },
    hasPreviewImage(preview) {
      return !!String(preview || "").trim();
    },
    getResourceThumb(item) {
      if (!item || typeof item !== "object") return "";
      const preview = String(item.preview || "").trim();
      if (preview) return preview;
      return String(item.avatar || "").trim();
    },
    isPlaceholderAsset(src) {
      const s = String(src || "").trim();
      if (!s) return true;
      return /dummyimage\.com/i.test(s);
    },
    randomPreviewUrl() {
      return (
        "https://dummyimage.com/420x620/" +
        Math.floor(Math.random() * 900 + 100) +
        "/111.png&text=" + Math.floor(Math.random() * 9 + 1)
      );
    },
    setResourceSubTab(tab) {
      if (tab === "role" || tab === "scene" || tab === "material") {
        this.resourceSubTab = tab;
      }
    },
    selectResourceItem(item) {
      if (!item) return;
      if (this.resourceSubTab === "role") this.activeRoleId = item.id;
      else if (this.resourceSubTab === "scene") this.activeSceneId = item.id;
      else this.activeMaterialId = item.id;
    },
    isResourceItemActive(item) {
      if (!item) return false;
      if (this.resourceSubTab === "role") return this.activeRoleId === item.id;
      if (this.resourceSubTab === "scene") return this.activeSceneId === item.id;
      return this.activeMaterialId === item.id;
    },
    itemPreviewLabel(item) {
      return this.hasPreviewImage(item && item.preview) ? "已完成" : "待生成";
    },
    async saveCurrentResource() {
      if (!this.currentResourceEntity) return;
      this.persistResourceCaches();
      try {
        await this.persistCurrentEntityToApi();
        const labels = { role: "角色", scene: "场景", material: "素材" };
        this.windowAlert(`${labels[this.resourceSubTab] || "资源"}信息已保存`);
      } catch (e) {
        this.windowAlert(e && e.message ? e.message : "保存失败");
      }
    },
    triggerEntityImageUpload() {
      if (!this.currentResourceEntity) return;
      const el = this.$refs.entityUploadInput;
      if (!el) return;
      try {
        el.value = "";
      } catch (e) {
        /* ignore */
      }
      el.click();
    },
    async onEntityImageSelected(e) {
      const entity = this.currentResourceEntity;
      if (!entity) return;
      const file = e && e.target && e.target.files ? e.target.files[0] : null;
      if (!file) return;
      if (!localStorage.getItem("user_token")) {
        this.windowAlert("请先登录再上传图片");
        return;
      }
      try {
        const r = await userApi.uploadAsset({
          file,
          assetType: this.resourceSubTab,
          name: entity && entity.name ? entity.name : "entity"
        });
        const url = r && (r.cover || r.url) ? String(r.cover || r.url) : "";
        if (!url.trim()) {
          this.windowAlert("上传失败：未返回图片地址");
          return;
        }
        entity.preview = url;
        if (!String(entity.avatar || "").trim() || this.isPlaceholderAsset(entity.avatar)) {
          entity.avatar = url;
        }
        this.persistResourceCaches();
        await this.persistCurrentEntityToApi();
        this.windowAlert("图片已上传并替换当前项");
      } catch (err) {
        this.windowAlert(err && err.message ? err.message : "上传失败");
      }
    },
    async persistCurrentEntityToApi() {
      const entity = this.currentResourceEntity;
      if (!entity) return;
      if (!localStorage.getItem("user_token") || !entity.id) return;
      clearTimeout(this._draftSaveTimer);
      await userApi.saveProjectEntity({
        id: entity.id,
        name: entity.name || "",
        age: entity.age || "",
        gender: entity.gender || "",
        summary: entity.summary || "",
        description: entity.description || "",
        avatar: entity.avatar || "",
        preview: entity.preview || ""
      });
    },
    downloadCurrentEntityImage() {
      const entity = this.currentResourceEntity;
      if (!entity) return;
      const u = String(entity.preview || "").trim();
      if (!u) return;
      window.open(u, "_blank");
    },
    /** 步骤二：实体图异步任务轮询（依赖 ai:step1-worker 或 taskListener 消费队列） */
    async pollEntityImageTaskUntilDone(taskId, intervalMs = 2000, maxWaitMs = 900000) {
      const started = Date.now();
      while (Date.now() - started < maxWaitMs) {
        const st = await userApi.getGenerateEntityImageTaskStatus({ taskId });
        const status = String(st.status || "");
        if (status === "success") {
          return st;
        }
        if (status === "failed") {
          throw new Error(st.errorMessage || st.message || "图片生成失败");
        }
        await new Promise(r => setTimeout(r, intervalMs));
      }
      throw new Error(
        "等待图片生成超时。请确认服务器正在运行：php think ai:step1-worker，或已配置定时访问 UserApi/taskListener"
      );
    },
    clearStep2PollTimer() {
      if (this._step2PollTimer) {
        clearInterval(this._step2PollTimer);
        this._step2PollTimer = null;
      }
    },
    /** 将步骤二异步逻辑串成单队列，避免 resume 的轮询与批量/单条 await 循环并发写同一列表 */
    runSerialStep2(fn) {
      const run = (this._step2Serial || Promise.resolve()).then(() => fn());
      this._step2Serial = run.catch(() => {});
      return run;
    },
    /** 切换项目或重新拉详情时清空步骤二队列 UI（服务端任务仍在库中，由 resume 再拉） */
    clearStep2BackgroundPoll() {
      this.clearStep2PollTimer();
      this.step2WatchList = [];
    },
    mergeStep2WatchJobs(newJobs) {
      if (!Array.isArray(newJobs) || !newJobs.length) return;
      const m = new Map(this.step2WatchList.map(x => [Number(x.taskId), x]));
      for (const j of newJobs) {
        const tid = Number(j.taskId);
        if (!tid) continue;
        m.set(tid, {
          taskId: tid,
          entityId: Number(j.entityId) || 0,
          entityName: (j.entityName && String(j.entityName)) || ""
        });
      }
      this.step2WatchList = Array.from(m.values());
    },
    findEntityItemById(entityId) {
      const id = Number(entityId);
      if (!id) return null;
      const all = [...this.roles, ...this.sceneItems, ...this.materialItems];
      for (const it of all) {
        if (it && Number(it.id) === id) return it;
      }
      return null;
    },
    applyPreviewToEntityId(entityId, preview) {
      const p = String(preview || "").trim();
      if (!p) return;
      const it = this.findEntityItemById(entityId);
      if (!it) return;
      it.preview = p;
      if (!String(it.avatar || "").trim() || this.isPlaceholderAsset(it.avatar)) {
        it.avatar = p;
      }
    },
    async tickStep2WatchOnce() {
      const jobs = Array.isArray(this.step2WatchList) ? [...this.step2WatchList] : [];
      if (!jobs.length) return true;
      const next = [];
      const hadLen = jobs.length;
      for (const job of jobs) {
        const tid = Number(job.taskId);
        if (!tid) continue;
        try {
          const st = await userApi.getGenerateEntityImageTaskStatus({ taskId: tid });
          const status = String(st.status || "");
          if (status === "success") {
            const preview = String(
              (st && st.preview) || (st && st.result && st.result.preview) || ""
            ).trim();
            if (preview) {
              this.applyPreviewToEntityId(job.entityId, preview);
            }
            continue;
          }
          if (status === "failed") {
            continue;
          }
          next.push(job);
        } catch (e) {
          next.push(job);
        }
      }
      this.step2WatchList = next;
      if (hadLen && !next.length) {
        this.clearStep2PollTimer();
        this.persistResourceCaches();
        this.notifyPointsChanged();
      }
      return next.length === 0;
    },
    async resumeStep2ImageTasksFromServer() {
      const pid = Number((this.project && this.project.id) || 0);
      if (!pid || !localStorage.getItem("user_token")) return;
      void this.runSerialStep2(async () => {
        let data;
        try {
          data = await userApi.listStep2EntityImageTasksForProject({ projectId: pid });
        } catch (e) {
          return;
        }
        if (Number((this.project && this.project.id) || 0) !== pid) return;
        const tasks = (data && data.tasks) || [];
        if (!tasks.length) return;
        this.mergeStep2WatchJobs(
          tasks
            .map(t => ({
              taskId: Number(t.taskId) || 0,
              entityId: Number(t.entityId) || 0,
              entityName: (t.entityName && String(t.entityName)) || ""
            }))
            .filter(j => j.taskId > 0 && j.entityId > 0)
        );
        if (!this.step2WatchList.length) return;
        try {
          await this.runStep2WatchLoopUntilEmpty(2000);
        } catch (e) {
          /* 刷新恢复时超时不弹窗打断用户 */
        }
      });
    },
    async runStep2WatchLoopUntilEmpty(intervalMs = 2000, maxWaitMs = 900000) {
      const t0 = Date.now();
      while (this.step2WatchList.length && Date.now() - t0 < maxWaitMs) {
        await this.tickStep2WatchOnce();
        if (this.step2WatchList.length) {
          await new Promise(r => setTimeout(r, intervalMs));
        }
      }
      if (this.step2WatchList.length) {
        throw new Error(
          "等待图片生成超时。请确认服务器正在运行：php think ai:step1-worker，或已配置定时访问 UserApi/taskListener"
        );
      }
      this.persistResourceCaches();
      this.notifyPointsChanged();
    },
    async handleBatchGenerate() {
      if (this.step2ImageWorkLocked) return;
      this.batchGenerating = true;
      try {
      const typeLabels = { role: "角色", scene: "场景", material: "素材" };
      const tab = this.resourceSubTab;
      const label = typeLabels[tab];

      await this.ensureGeneratedData();

      const list = tab === "role" ? this.roles : tab === "scene" ? this.sceneItems : this.materialItems;
      if (!list.length) {
        this.windowAlert(`暂无${label}数据。请先在步骤 1 填写剧本以生成示例列表，或通过缓存恢复项目。`);
        return;
      }

      // 已登录：先为每条实体创建 fa_ai_project_task（全部落库），再统一轮询；刷新页面可由 resume 接续
      if (localStorage.getItem("user_token")) {
        if (this.step2SinglePointsValue <= 0) {
          this.windowAlert(
            "当前图片模型未配置步骤二扣费，请在后端 fa_ai_points_config 中维护 scene_code=step2_generate_image 的积分规则。"
          );
          return;
        }
        // 仅补全未生成的空图：已有图（含手动上传）不重复生成
        const targets = list.filter(it => it && it.id && !this.hasPreviewImage(it.preview));
        if (!targets.length) {
          this.windowAlert(`当前${label}均已有图片，无需批量生成`);
          return;
        }
        await this.runSerialStep2(async () => {
          const pid = Number((this.project && this.project.id) || 0);
          if (!pid) return;
          const jobs = [];
          let pollMs = 2000;
          for (const it of targets) {
            try {
              const created = await userApi.generateEntityImage({ entityId: it.id });
              const taskId = created && created.taskId;
              if (!taskId) continue;
              if (created.pollIntervalMs && created.pollIntervalMs > 0) {
                pollMs = created.pollIntervalMs;
              }
              jobs.push({
                taskId,
                entityId: it.id,
                entityName: (it.name && String(it.name)) || ""
              });
            } catch (e) {
              /* 单条创建失败跳过 */
            }
          }
          if (!jobs.length) {
            this.windowAlert("创建图片任务失败，请检查网络、积分与模型配置");
            return;
          }
          if (Number((this.project && this.project.id) || 0) !== pid) return;
          this.mergeStep2WatchJobs(jobs);
          try {
            await this.runStep2WatchLoopUntilEmpty(pollMs);
          } catch (e) {
            this.windowAlert(e && e.message ? e.message : "部分任务未完成");
          }
          const ok = targets.filter(it => this.hasPreviewImage(it.preview)).length;
          this.persistResourceCaches();
          if (ok > 0) {
            this.notifyPointsChanged();
          }
          this.windowAlert(ok ? `已生成 ${ok} 条${label}预览图` : `生成失败：请检查图片模型配置与密钥`);
        });
        return;
      }

      const missing = list.filter(i => !this.hasPreviewImage(i.preview));
      if (missing.length === 0) {
        if (!window.confirm(`当前${label}预览图均已生成，是否全部重新生成？`)) return;
        list.forEach(i => {
          i.preview = this.randomPreviewUrl();
        });
        this.persistResourceCaches();
        this.windowAlert(`已重新生成全部${label}预览图`);
        return;
      }

      if (missing.length < list.length) {
        missing.forEach(i => {
          i.preview = this.randomPreviewUrl();
        });
        this.persistResourceCaches();
        this.windowAlert(`已为 ${missing.length} 条${label}补全预览图`);
        return;
      }

      list.forEach(i => {
        i.preview = this.randomPreviewUrl();
      });
      this.persistResourceCaches();
      this.windowAlert(`已生成全部${label}预览图`);
      } finally {
        this.batchGenerating = false;
      }
    },
    openRoleModal(role) {
      this.roleModal.visible = true;
      this.roleModal.mode = role ? "edit" : "create";
      this.roleModal.targetId = role ? role.id : null;
      this.roleForm = role
        ? {
            name: role.name,
            age: role.age,
            gender: role.gender,
            summary: role.summary,
            description: role.description
          }
        : {
            name: "",
            age: "",
            gender: "",
            summary: "",
            description: ""
          };
    },
    closeRoleModal() {
      this.roleModal.visible = false;
    },
    submitRoleModal() {
      if (!this.roleForm.name) {
        this.windowAlert("请输入角色名");
        return;
      }
      if (this.roleModal.mode === "create") {
        const newId = Date.now();
        const newRole = {
          id: newId,
          name: this.roleForm.name,
          age: this.roleForm.age || "待补充",
          gender: this.roleForm.gender || "待补充",
          summary: this.roleForm.summary || "暂无摘要",
          description: this.roleForm.description || "暂无描述",
          avatar: `https://dummyimage.com/80x110/3f4f58/fff.png&text=${this.roles.length + 1}`,
          preview: ""
        };
        this.roles.unshift(newRole);
        this.activeRoleId = newId;
      } else {
        this.roles = this.roles.map(item =>
          item.id === this.roleModal.targetId
            ? {
                ...item,
                name: this.roleForm.name,
                age: this.roleForm.age,
                gender: this.roleForm.gender,
                summary: this.roleForm.summary,
                description: this.roleForm.description
              }
            : item
        );
      }
      this.persistResourceCaches();
      this.closeRoleModal();
    },
    async regenerateCurrentResource() {
      if (this.step2ImageWorkLocked) return;
      this.resourceGenerating = true;
      try {
      const entity = this.currentResourceEntity;
      if (!entity) return;
      const hadPreview = this.entityHasPreviewImage;
      if (localStorage.getItem("user_token") && entity.id) {
        if (this.step2SinglePointsValue <= 0) {
          this.windowAlert(
            "当前图片模型未配置步骤二扣费，请在后端 fa_ai_points_config 中维护 scene_code=step2_generate_image 的积分规则。"
          );
          return;
        }
        try {
          // 重新生成前先把当前表单内容落库，避免后端读取旧 description
          this.persistResourceCaches();
          await this.persistCurrentEntityToApi();
          const created = await userApi.generateEntityImage({ entityId: entity.id });
          const taskId = created && created.taskId;
          if (!taskId) {
            this.windowAlert("创建图片任务失败");
            return;
          }
          const interval =
            created.pollIntervalMs && created.pollIntervalMs > 0 ? created.pollIntervalMs : 2000;
          const eid = Number(entity.id) || 0;
          await this.runSerialStep2(async () => {
            const pid = Number((this.project && this.project.id) || 0);
            if (!pid) return;
            this.mergeStep2WatchJobs([
              {
                taskId,
                entityId: eid,
                entityName: (entity.name && String(entity.name)) || ""
              }
            ]);
            const st = await this.pollEntityImageTaskUntilDone(taskId, interval);
            const stillHere = Number((this.project && this.project.id) || 0) === pid;
            const preview = String(
              (st && st.preview) || (st && st.result && st.result.preview) || ""
            ).trim();
            if (stillHere && preview) {
              entity.preview = preview;
              if (!String(entity.avatar || "").trim() || this.isPlaceholderAsset(entity.avatar)) {
                entity.avatar = preview;
              }
              this.persistResourceCaches();
              this.notifyPointsChanged();
            }
            this.step2WatchList = (this.step2WatchList || []).filter(
              j => Number(j.taskId) !== Number(taskId)
            );
            if (!this.step2WatchList.length) {
              this.clearStep2PollTimer();
            }
          });
        } catch (e) {
          this.windowAlert(e && e.message ? e.message : "生成失败");
          return;
        }
      } else {
        entity.preview = this.randomPreviewUrl();
        this.persistResourceCaches();
      }
      void hadPreview;
      } finally {
        this.resourceGenerating = false;
      }
    },
    regenerateRole() {
      this.regenerateCurrentResource();
    },
    persistStoryboardsCache() {
      sessionStorage.setItem(this.ssKey("storyboards"), JSON.stringify(this.storyboards));
      this.scheduleSaveProjectDraft();
    },
    goToStoryboardEditor() {
      if (!this.storyboards.length) {
        this.windowAlert("请先生成分集列表");
        return;
      }
      const first = this.storyboards[0];
      this.persistStoryboardsCache();
      this.$router.push({
        path: "/project/storyboard-editor",
        query: {
          id: String(first.id),
          projectId: String(this.project.id)
        }
      });
    },
    openEpisodeModal() {
      this.episodeModal.visible = true;
      this.episodeModal.title = "";
      this.episodeModal.content = "";
    },
    closeEpisodeModal() {
      this.episodeModal.visible = false;
    },
    openEntityPreview(entity) {
      if (!entity) return;
      const preview = String(entity.preview || "").trim();
      if (!preview) return;
      if (this.isPlaceholderAsset(preview)) return;

      this.entityPreviewDialog.visible = true;
      this.entityPreviewDialog.src = preview;
      this.entityPreviewDialog.alt = String(entity.name || "预览");
      this.entityPreviewDialog.entity = entity;
      this.entityPreviewDialog.field = "preview";
    },
    closeEntityPreviewDialog() {
      this.entityPreviewDialog.visible = false;
      this.entityPreviewDialog.src = "";
      this.entityPreviewDialog.alt = "";
      this.entityPreviewDialog.entity = null;
    },
    onEntityPreviewImgError() {
      const e = this.entityPreviewDialog.entity;
      if (e) this.onImgError(e, this.entityPreviewDialog.field || "preview");
    },
    closeStoryboardCoverPreview() {
      this.storyboardCoverPreview = {
        visible: false,
        src: "",
        alt: "",
        item: null
      };
    },
    onStoryboardCoverPreviewImgError() {
      const item = this.storyboardCoverPreview.item;
      if (item) this.onImgError(item, "cover");
    },
    submitEpisodeModal() {
      const content = (this.episodeModal.content || "").trim();
      if (!content) {
        this.windowAlert("请输入本分集内容");
        return;
      }
      if (content.length > 10000) {
        this.windowAlert("本分集内容不超过1万字");
        return;
      }
      const startLen = this.storyboards.length;
      const episodeNum = startLen + 1;
      const title = (this.episodeModal.title || "").trim() || `第 ${episodeNum} 集：新分集`;
      this.storyboards.push({
        id: Date.now(),
        title,
        roles: String(2 + ((episodeNum - 1) % 5)),
        scenes: "1",
        duration: "待定",
        cover: `https://dummyimage.com/108x146/3a3a3a/fff.png&text=${episodeNum}`,
        episodeScript: content
      });
      this.persistStoryboardsCache();
      this.closeEpisodeModal();
      this.windowAlert("已新增 1 条分集");
    },
    async handleRegenerateStoryboardScript() {
      if (!this.scriptContent) {
        this.windowAlert("请先在步骤 1 填写剧本内容后再生成分镜脚本。");
        return;
      }
      if (this.storyboards.length) {
        if (!window.confirm("重新生成将替换当前所有分集及分集下的所有视频记录，是否继续？")) return;
      } else if (!window.confirm("将根据剧本生成分集列表（当前为服务端占位解析，后续接真实语言模型），是否继续？")) {
        return;
      }
      try {
        if (localStorage.getItem("user_token")) {
          const r = await userApi.generateProjectStep3Storyboards({ id: this.project.id });
          this.storyboards = Array.isArray(r.storyboards) ? r.storyboards : [];
        } else {
          this.storyboards = this.storyboardTemplates.map((item, idx) => ({
            ...item,
            id: Date.now() + idx
          }));
        }
        this.persistStoryboardsCache();
        clearTimeout(this._draftSaveTimer);
        await this.flushProjectDraftToApi();
        this.windowAlert("分镜脚本列表已更新");
      } catch (e) {
        this.windowAlert(e && e.message ? e.message : "生成失败");
      }
    },
    confirmDeleteStoryboard(item) {
      if (!item) return;
      if (!window.confirm(`将删除分集「${item.title}」及该分集下的所有视频记录，是否继续？`)) return;
      this.storyboards = this.storyboards.filter(row => row.id !== item.id);
      this.persistStoryboardsCache();
    },
    confirmDeleteRole(item) {
      if (!item) return;
      if (!window.confirm(`确认删除角色「${item.name || "图片"}」吗？`)) return;
      const wasActive = this.activeRoleId === item.id;
      this.roles = this.roles.filter(r => r.id !== item.id);
      this.persistResourceCaches();
      if (wasActive) {
        this.activeRoleId = this.roles[0] ? this.roles[0].id : null;
      }
    },
    openStoryboardPreview(item) {
      if (!item) return;
      const cover = String(item.cover || "").trim();
      if (!cover || this.isPlaceholderAsset(cover)) return;
      this.storyboardCoverPreview = {
        visible: true,
        src: cover,
        alt: String(item.title || "预览"),
        item
      };
    },
    openStoryboardEdit(item) {
      // 第三步的“编辑”改为跳转独立页面，而不是继续使用弹窗。
      this.persistStoryboardsCache();
      this.$router.push({
        path: "/project/storyboard-editor",
        query: {
          id: item.id,
          projectId: this.project.id
        }
      });
    },
    openStoryboardExport(item) {
      this.downloadStoryboard(item);
    },
    downloadStoryboard(item) {
      if (!item) return;
      const payload = {
        exportedAt: new Date().toISOString(),
        projectId: this.project && this.project.id ? this.project.id : null,
        storyboard: item
      };
      const safeTitle = String(item.title || "storyboard")
        .replace(/[\\/:*?"<>|]/g, "_")
        .replace(/\s+/g, " ")
        .trim();
      const filename = `${safeTitle || "storyboard"}-${item.id}.json`;
      const blob = new Blob([JSON.stringify(payload, null, 2)], { type: "application/json;charset=utf-8" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    },
    // submitStoryboardDialog: export 弹窗已移除（保留空实现会产生误导）
  }
};
</script>

<style scoped>
.setup-page {
  height: calc(100vh - 40px);
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.setup-header {
  display: flex;
  justify-content: center;
  gap: 14px;
  margin-bottom: 18px;
  flex-shrink: 0;
}

.setup-fixed-actions {
  position: absolute;
  top: 14px;
  right: 18px;
  z-index: 5;
  display: flex;
  gap: 10px;
  align-items: center;
}

.setup-fixed-actions .main-btn,
.setup-fixed-actions .secondary-btn {
  width: auto;
  flex-shrink: 0;
}

.step1-processing-tip {
  padding: 6px 10px;
  border-radius: 10px;
  font-size: 12px;
  color: #b8f0cf;
  background: rgba(29, 125, 82, 0.2);
  border: 1px solid rgba(29, 125, 82, 0.45);
}

.step2-task-queue-hint {
  margin: 8px 0 10px;
  padding: 8px 10px;
  border-radius: 10px;
  font-size: 12px;
  line-height: 1.45;
  color: #d4e8ff;
  background: rgba(45, 108, 168, 0.22);
  border: 1px solid rgba(90, 160, 230, 0.45);
}

.step-fill {
  flex: 1;
  min-height: 0;
  overflow: hidden;
}

.step {
  min-width: 120px;
  padding: 10px 14px;
  background: #242424;
  border-radius: 20px;
  color: #9b9b9b;
  text-align: center;
  font-size: 13px;
  border: 0;
  cursor: pointer;
}

.step.active {
  color: #fff;
  background: #19b06b;
}

.setup-body {
  display: grid;
  grid-template-columns: 360px 1fr;
  gap: 18px;
  height: 100%;
  min-height: 0;
}

.setup-sidebar,
.setup-main {
  background: #333333;
  border-radius: 14px;
  padding: 10px;
  min-height: 0;
}

.setup-sidebar {
  overflow-y: auto;
}

.setup-main {
  overflow-y: auto;
}

.setup-block + .setup-block {
  margin-top: 18px;
}

.setup-label {
  margin-bottom: 8px;
  color: #d6d6d6;
}

.setup-hint {
  margin-top: 8px;
  font-size: 12px;
  line-height: 1.45;
  color: #8a8a8a;
}

.setup-title-input {
  width: 100%;
  height: 40px;
  box-sizing: border-box;
  padding: 0 12px;
  border-radius: 8px;
  border: 1px solid #3d3d3d;
  background: #1f1f1f;
  color: #fff;
}

.setup-select-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 10px;
}

.setup-ratio-grid .setup-select-card {
  min-height: 48px;
}

.setup-select-card {
  background: #252525;
  border: 1px solid #3e3e3e;
  border-radius: 10px;
  color: #e8e8e8;
  cursor: pointer;
  padding: 12px;
  text-align: left;
}

.setup-select-card.active {
  border-color: #19b06b;
  box-shadow: inset 0 0 0 1px #19b06b;
}

.setup-type-card {
  min-height: 70px;
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.setup-type-card span {
  color: #9f9f9f;
  font-size: 12px;
}

.setup-value {
  height: 40px;
  display: flex;
  align-items: center;
  padding: 0 12px;
  border-radius: 8px;
  background: #1f1f1f;
}

.script-type-row {
  display: flex;
  gap: 10px;
  flex-wrap: wrap;
}

.script-type {
  background: #1f1f1f;
  color: #d9d9d9;
  border: 1px solid #3f3f3f;
  border-radius: 8px;
  padding: 10px 14px;
}

.script-type.active,
.style-card.active {
  border-color: #19b06b;
  box-shadow: inset 0 0 0 1px #19b06b;
}

.script-type.disabled {
  opacity: 0.55;
  cursor: not-allowed;
}

.style-grid {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 1px;
}

.style-card {
  background: #232323;
  border: 1px solid #3c3c3c;
  border-radius: 10px;
  padding: 5px;
  cursor: pointer;
}

.style-card-cover {
  width: 100%;
  border-radius: 10px;
  margin-bottom: 6px;
  aspect-ratio: 1 / 1;
  object-fit: cover;
}

.style-card span {
  display: block;
  text-align: center;
  font-size: 12px;
}

.script-editor {
  width: 100%;
  min-height: 360px;
  border: 1px solid #3a3a3a;
  border-radius: 10px;
  background: #1a1a1a;
  color: #fff;
  padding: 14px;
  resize: none;
  margin-top: 14px;
}

.script-editor.disabled {
  opacity: 0.7;
  cursor: not-allowed;
}

.script-char-count {
  margin-top: 8px;
  font-size: 12px;
  color: #9a9a9a;
}

.script-char-count.over {
  color: #e85a5a;
}

.script-char-hint {
  margin-left: 8px;
}

.script-episode-block {
  margin-top: 16px;
}

.episode-count-hint {
  margin: 0 0 10px;
  font-size: 12px;
  line-height: 1.45;
  color: #9f9f9f;
}

.episode-count-row {
  display: flex;
  flex-wrap: nowrap;
  align-items: center;
  min-height: 40px;
}

.episode-radios {
  display: flex;
  flex-wrap: nowrap;
  align-items: center;
  gap: 18px;
  min-height: 40px;
  width: 100%;
  overflow-x: auto;
  scrollbar-width: none;
  -ms-overflow-style: none;
}

.episode-radios::-webkit-scrollbar {
  display: none;
}

/** 与「自定义集数」单选同一行，输入紧挨文字右侧 */
.episode-custom-inline {
  display: inline-flex;
  flex-wrap: nowrap;
  align-items: center;
  gap: 10px;
  min-height: 40px;
  flex-shrink: 0;
}

.episode-radio-label {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  cursor: pointer;
  color: #d6d6d6;
  font-size: 14px;
  user-select: none;
}

.episode-radio-label input[type="radio"] {
  width: 16px;
  height: 16px;
  accent-color: #19b06b;
  cursor: pointer;
}

.episode-count-input-wrap {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  flex-shrink: 0;
}

.episode-count-input {
  width: 72px;
  height: 36px;
  box-sizing: border-box;
  padding: 0 10px;
  border-radius: 8px;
  border: 1px solid #3d3d3d;
  background: #1f1f1f;
  color: #fff;
  font-size: 14px;
}

.episode-count-input:focus {
  outline: none;
  border-color: #19b06b;
}

.episode-count-suffix {
  color: #9f9f9f;
  font-size: 14px;
}

.setup-bottom-bar {
  display: flex;
  justify-content: space-between;
  gap: 12px;
  margin-top: 16px;
}

.setup-submit {
  max-width: 220px;
}

.setup-bottom-bar,
.footer-step-actions {
  display: none;
}

.empty-panel {
  min-height: 220px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #9c9c9c;
  background: #232323;
  border: 1px dashed #3c3c3c;
  border-radius: 10px;
}

.role-layout {
  display: grid;
  grid-template-columns: 260px 1fr 260px;
  gap: 10px;
  height: 100%;
  min-height: 0;
}

.role-list-panel,
.role-preview-panel,
.role-detail-panel,
.storyboard-layout {
  background: #333333;
  border-radius: 14px;
  padding: 10px;
  min-height: 0;
  overflow: hidden;
}

.role-list-panel {
  display: flex;
  flex-direction: column;
}

.role-panel-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 14px;
  flex-wrap: nowrap;
  min-width: 0;
}

.role-panel-header h3 {
  flex: 1;
  min-width: 0;
  margin: 0;
  font-size: 1.05rem;
  font-weight: 600;
  line-height: 1.3;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.role-header-actions {
  display: flex;
  gap: 8px;
  flex-shrink: 0;
  align-items: center;
  flex-wrap: wrap;
  justify-content: flex-end;
}

.resource-subnav {
  display: flex;
  gap: 6px;
  margin-bottom: 12px;
  padding: 4px 8px;
  background: #252525;
  border-radius: 10px;
  border: 1px solid #3b3b3b;
  min-width: 0;
  flex-wrap: wrap;
}

.resource-subnav-item {
  flex: 1;
  padding: 8px 10px;
  border: none;
  border-radius: 8px;
  background: transparent;
  color: #b8b8b8;
  font-size: 13px;
  cursor: pointer;
  transition: background 0.15s, color 0.15s;
}

.resource-subnav-item:hover {
  color: #e6e6e6;
  background: rgba(255, 255, 255, 0.04);
}

.resource-subnav-item.active {
  background: #303030;
  color: #19b06b;
  box-shadow: inset 0 0 0 1px #19b06b55;
}

.role-list {
  display: flex;
  flex-direction: column;
  gap: 12px;
  flex: 1;
  min-height: 0;
  overflow-y: auto;
}

.role-list-item {
  position: relative;
  display: flex;
  gap: 10px;
  padding: 10px 10px 10px 10px;
  border: 1px solid #3b3b3b;
  border-radius: 10px;
  background: #252525;
  cursor: pointer;
}

.role-delete-icon {
  position: absolute;
  right: 8px;
  bottom: 8px;
  width: 28px;
  height: 28px;
  border-radius: 8px;
  border: 1px solid #5a3a3a;
  background: rgba(0, 0, 0, 0.35);
  color: #f0b0b0;
  font-size: 14px;
  line-height: 1;
  cursor: pointer;
  padding: 0;
  display: flex;
  align-items: center;
  justify-content: center;
}

.role-delete-icon:hover {
  background: #3a2222;
}

.role-list-item.active {
  border-color: #19b06b;
  box-shadow: inset 0 0 0 1px #19b06b;
}

.role-list-thumb {
  width: 50px;
  height: 50px;
  object-fit: cover;
  border-radius: 10px;
  background: #f0f0f0;
}

.role-list-content {
  flex: 1;
  min-width: 0;
}

.role-list-top {
  display: flex;
  justify-content: space-between;
  gap: 8px;
  margin-bottom: 6px;
}
.role-list-top strong{
flex:1;
font-size: 12px;
}

.role-list-content p {
  margin: 0;
  font-size: 12px;
  color: #a9a9a9;
  line-height: 1.5;
}

.role-status {
  color: #19b06b;
  font-size: 12px;
}

.role-preview-wrap {
  display: flex;
  flex-direction: column;
  align-items: center;
  height: 100%;
  min-height: 0;
}

.role-preview-panel {
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.role-preview-image {
  width: 100%;
  //max-width: 550px;
  min-width:300px;
  flex: 1;
  min-height: 0;
  object-fit: contain;
  border-radius: 10px;
  cursor: zoom-in;
}

/* 复用 RechargeModal 的关闭按钮风格 */
.modal-close {
  position: fixed;
  right: 16px;
  top: 14px;
  width: 36px;
  height: 36px;
  border: none;
  border-radius: 8px;
  background: rgba(255, 255, 255, 0.3);
  color: #fff;
  font-size: 22px;
  line-height: 1;
  cursor: pointer;
  z-index: 10051;
}

.placeholder-block {
  background: #333333 !important;
}

.role-preview-placeholder {
  flex: 1;
  min-height: 200px;
  max-width: 500px;
  width: 100%;
  margin: 0 auto;
  display: flex;
  align-items: center;
  justify-content: center;
  text-align: center;
  padding: 10px;
  border-radius: 10px;
  border: 1px dashed #4a4a4a;
  color: #9a9a9a;
  font-size: 13px;
  line-height: 1.5;
  box-sizing: border-box;
}

.role-preview-actions {
  margin-top: 14px;
  display: flex;
  gap: 10px;
  flex-wrap: wrap;
  justify-content: center;
  flex-shrink: 0;
}

.role-empty,
.storyboard-empty {
  min-height: 420px;
}

.role-detail-form {
  display: flex;
  flex-direction: column;
  gap: 14px;
  height: 100%;
  overflow-y: auto;
}

.detail-field label {
  display: block;
  margin-bottom: 8px;
  color: #dfdfdf;
}

.detail-field input,
.detail-field textarea {
  width: 100%;
  background: #1f1f1f;
  border: 1px solid #3c3c3c;
  border-radius: 8px;
  color: #fff;
  padding: 10px 12px;
}

.detail-field textarea {
  resize: vertical;
}

.detail-actions {
  display: flex;
  gap: 10px;
  font-size:12px;
}
.main-btn{
  font-size:12px;
  min-width:70px;
}
.role-regenerate-points {
  margin-left: 4px;
  font-size: 12px;
  font-weight: 400;
}

.storyboard-header {
  display: flex;
  justify-content: space-between;
  gap: 12px;
  align-items: flex-start;
  margin-bottom: 18px;
  flex-shrink: 0;
}

.storyboard-header-left h2 {
  margin: 0 0 6px;
}

.storyboard-header-actions {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  justify-content: flex-end;
  align-items: center;
}

.storyboard-layout {
  display: flex;
  flex-direction: column;
}

.storyboard-content-scroll {
  flex: 1;
  min-height: 0;
  overflow-y: auto;
}

.batch-btn {
  max-width: 140px;
}

.storyboard-header-actions .batch-btn {
  max-width: none;
}

.storyboard-grid {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 18px;
}

.storyboard-card {
  display: grid;
  grid-template-columns: 18px 150px 1fr;
  gap: 10px;
  padding: 10px;
  border: 1px solid #3a3a3a;
  border-radius: 14px;
  background: #252525;
}

.storyboard-index {
  display: flex;
  align-items: center;
  justify-content: center;
  color: #d0d0d0;
  font-weight: 700;
}

.storyboard-cover {
  width: 150px;
  height: 100px;
  border-radius: 10px;
  object-fit: cover;
}

.storyboard-content h3 {
  margin: 0 0 10px;
  font-size: 16px;
}

.storyboard-meta {
  display: flex;
  gap: 12px;
  flex-wrap: wrap;
  color: #a9a9a9;
  font-size: 13px;
}

.storyboard-actions {
  display: flex;
  gap: 10px;
  margin-top: 18px;
  flex-wrap: wrap;
}

.story-btn {
  border: 1px solid #444;
  border-radius: 20px;
  background: #1f1f1f;
  color: #f0f0f0;
  padding: 8px 16px;
  cursor: pointer;
}

.story-btn.primary {
  background: #000;
  border-color: #000;
}

.story-btn-danger {
  border-color: #6b2f2f;
  color: #ffb8b8;
}

.modal-field-block {
  margin-top: 14px;
}

.modal-field-block label {
  display: block;
  margin-bottom: 8px;
  color: #d0d0d0;
  font-size: 13px;
}

.modal-field-block input {
  width: 100%;
  box-sizing: border-box;
  height: 40px;
  padding: 0 12px;
  border-radius: 8px;
  border: 1px solid #3d3d3d;
  background: #1f1f1f;
  color: #fff;
}

.episode-modal .modal-field-block .episode-modal-script {
  min-height: 200px;
  max-height: min(360px, 50vh);
  margin-top: 0;
  overflow-y: auto;
  resize: vertical;
}

.episode-content-count {
  margin-top: 6px;
  text-align: right;
  font-size: 12px;
  color: #8c8c8c;
}

.modal-textarea {
  width: 100%;
  background: #1f1f1f;
  border: 1px solid #444;
  border-radius: 8px;
  color: #fff;
  padding: 10px;
  resize: vertical;
}

.modal-actions {
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  margin-top: 0px;
}

.modal-main-btn {
  max-width: 150px;
}

.modal-inline-fields {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 10px;
}

.storyboard-export-wrap {
  min-height: 120px;
  display: flex;
  flex-direction: column;
  justify-content: center;
}

@media (max-width: 768px) {
  .setup-page {
    height: auto;
    min-height: calc(100vh - 40px);
    max-height: none;
    overflow-y: auto;
    -webkit-overflow-scrolling: touch;
  }

  .step-fill {
    overflow: visible;
    min-height: min(70vh, 520px);
  }

  .setup-header {
    flex-wrap: wrap;
    justify-content: center;
    gap: 10px;
    padding: 0 8px;
    margin-bottom: 12px;
  }

  .setup-fixed-actions {
    position: static;
    width: 100%;
    justify-content: center;
    flex-wrap: wrap;
    padding: 0 10px 10px;
    box-sizing: border-box;
  }

  .step {
    min-width: 0;
    flex: 1 1 calc(33.333% - 10px);
    padding: 8px 5px;
    font-size: 12px;
  }

  .setup-body {
    grid-template-columns: 1fr;
    gap: 12px;
    height: auto;
    min-height: 0;
  }

  .setup-sidebar,
  .setup-main {
    min-height: 0;
    overflow: visible;
  }

  .style-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  .episode-count-row,
  .episode-radios {
    flex-wrap: wrap;
  }

  .episode-custom-inline {
    flex-wrap: wrap;
  }

  .role-layout {
    grid-template-columns: 1fr;
    height: auto;
    min-height: 0;
  }

  .role-panel-header {
    flex-wrap: wrap;
    align-items: flex-start;
  }

  .role-panel-header h3 {
    white-space: normal;
    overflow: visible;
    text-overflow: clip;
    width: 100%;
  }

  .role-header-actions {
    width: 100%;
    justify-content: flex-start;
  }

  .role-preview-image {
    min-width: 0;
    max-width: 100%;
  }

  .role-preview-placeholder {
    max-width: none;
    min-height: 160px;
  }

  .role-empty,
  .storyboard-empty {
    min-height: 220px;
  }

  .detail-actions {
    flex-wrap: wrap;
  }

  .storyboard-header {
    flex-direction: column;
    align-items: stretch;
    gap: 10px;
  }

  .storyboard-header-actions {
    justify-content: flex-start;
  }

  .storyboard-grid {
    grid-template-columns: 1fr;
    gap: 12px;
  }

  .storyboard-card {
    display: flex;
    flex-direction: column;
    align-items: stretch;
  }

  .storyboard-index {
    align-self: flex-start;
  }

  .storyboard-cover {
    width: 100%;
    height: auto;
    aspect-ratio: 16 / 9;
    max-width: none;
  }

  .modal-inline-fields {
    grid-template-columns: 1fr;
  }
}
</style>
